import React, { useState, useEffect, useCallback } from 'react';
import SearchableSelect from '../components/ui/SearchableSelect';
import * as XLSX from 'xlsx';
import { saveStyledXlsx } from '../lib/excelStyle';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';
import {
  Plus, X, Edit2, Trash2, Search, Download, Upload,
  DollarSign, BookOpen, RefreshCw
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────
const COMPANIES   = ['ARMSTECH', 'SLCCS'];
const PAY_MODES   = ['EKM FDO','EKM Admin','TVM FDO','TVM Admin','Cash','HDFC Vazhakkala','HDFC Kottarakkara','SBI','Ujjivan'];
const TYPES       = ['Cash In', 'Cash Out'];
const CATEGORIES_IN  = ['Tuition Fees','Hostel Fees','Exam Fees','Manpower Fees','Registration Fees','Salary Recovery','Other Income'];
const CATEGORIES_OUT = ['Salary','Salary Advance','TA (Travel Allowance)','Food Expenses','Marketing','Rent','Utilities','Office Expenses','Miscellaneous'];
const ALL_CATS    = [...CATEGORIES_IN, ...CATEGORIES_OUT];
const MONTHS      = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Shared date-preset filter: Today / Yesterday / This Month / Last Month / Custom ──
function getPresetRange(preset, customFrom, customTo) {
  const pad = n => String(n).padStart(2,'0');
  const toStr = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const now = new Date();
  if (preset === 'today') {
    const s = toStr(now);
    return { from: s, to: s };
  }
  if (preset === 'yesterday') {
    const y = new Date(now); y.setDate(y.getDate()-1);
    const s = toStr(y);
    return { from: s, to: s };
  }
  if (preset === 'this_month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last  = new Date(now.getFullYear(), now.getMonth()+1, 0);
    return { from: toStr(first), to: toStr(last) };
  }
  if (preset === 'last_month') {
    const first = new Date(now.getFullYear(), now.getMonth()-1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: toStr(first), to: toStr(last) };
  }
  if (preset === 'custom') {
    return { from: customFrom||'', to: customTo||'' };
  }
  return { from: '', to: '' }; // 'all' / unrecognized
}

function DatePresetFilter({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, showAllTime=true }) {
  return (
    <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
      <select style={{width:130,fontSize:12,padding:'4px 6px'}} value={preset} onChange={e=>setPreset(e.target.value)}>
        {showAllTime&&<option value="all">All Time</option>}
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="this_month">This Month</option>
        <option value="last_month">Last Month</option>
        <option value="custom">Custom Range</option>
      </select>
      {preset==='custom'&&<>
        <DateInput value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{width:140,fontSize:12,padding:'4px 6px'}} placeholder="From"/>
        <DateInput value={customTo}   onChange={e=>setCustomTo(e.target.value)}   style={{width:140,fontSize:12,padding:'4px 6px'}} placeholder="To"/>
      </>}
    </div>
  );
}

const pad2 = n => String(n).padStart(2,'0');
const dateStrOf = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const todayStr = () => dateStrOf(new Date());
const dateStrPlus = days => { const d = new Date(); d.setDate(d.getDate()+days); return dateStrOf(d); };
const YEARS = (() => { const a = []; for (let y = 2021; y <= new Date().getFullYear() + 1; y++) a.push(y); return a; })();

// Reads EVERY row of a query (Supabase caps one request at 1000 rows). `build` must return a fresh query
// with a stable ORDER (always end with .order('id')) so pages never overlap or skip rows.
async function fetchAllRows(build) {
  const PAGE = 1000, BATCH = 6;
  const page = async (n) => {
    const { data, error } = await build().range(n * PAGE, n * PAGE + PAGE - 1);
    if (error) throw error;
    return data || [];
  };
  const out = [...await page(0)];
  if (out.length < PAGE) return out;
  // the rest: several pages at the same time (kept in order), stop at the first short page
  for (let n = 1; ; n += BATCH) {
    const pages = await Promise.all(Array.from({ length: BATCH }, (_, i) => page(n + i)));
    for (const p of pages) { out.push(...p); if (p.length < PAGE) return out; }
  }
}

// ── Who may approve expenses / issue petty cash / manage categories & locks. Edit the lists here (one place). ──
const ADMIN_EMAILS    = ['ershadarmstech@gmail.com', 'armstechac@gmail.com'];
const APPROVER_EMAILS = [...ADMIN_EMAILS, 'arsha@armstech.in', 'nisha@armstech.in', 'armstechekm@gmail.com'];
const isApproverUser  = u => !!u?.email && APPROVER_EMAILS.includes(String(u.email).toLowerCase());

// Petty-cash settlements (cash handed back by staff) are posted to the cash book as Cash In when true.
// Set to false if issuing petty cash is NOT recorded as a Cash Out in your books (otherwise settlements inflate cash).
const POST_SETTLEMENT_TO_CASHBOOK = false;
const TRANSFER_CATEGORY = 'Internal Transfer';

// ── Audit log (table accounts_audit_log) - best effort, silently off when the table does not exist ──
let _auditOk = null;
async function logAudit(user, action, entity, row, extra = {}) {
  try {
    if (_auditOk === false) return;
    const { error } = await supabase.from('accounts_audit_log').insert({
      action, entity, entity_id: row?.id != null ? String(row.id) : null,
      company: row?.company || extra.company || null, summary: extra.summary || null,
      before_data: extra.before || null, after_data: extra.after || null, changed_by: user?.email || null,
    });
    _auditOk = !error;
  } catch (e) { /* audit must never block the real action */ }
}
// PT-DEFAULT: front-desk / admin accounts default to Cash, every other account to Online
const defaultPayType = acct => { const k = String(acct || '').toUpperCase().replace(/[^A-Z]/g, ''); return ['EKMFDO','EKMADMIN','TVMFDO','TVMADMIN','CASH'].includes(k) ? 'Cash' : 'Online'; };
const txnSnap = r => r ? ({ date:r.date, type:r.type, category:r.category, payment_mode:r.payment_mode, payment_type:r.payment_type, party_name:r.party_name, amount:Number(r.amount), reference:r.reference, description:r.description, entry_by:r.entry_by, company:r.company, project_id:r.project_id, source:r.source }) : null;
const txnSummary = r => `${r.type} ₹${Number(r.amount||0).toLocaleString('en-IN')} · ${r.category||'-'} · ${r.date}${r.party_name?' · '+r.party_name:''}`;

// ── Month-end lock (table accounts_period_locks): nothing dated on/before the lock date can be added, changed or deleted ──
let _lockOk = null;
async function getLockedThrough(company) {
  if (_lockOk === false) return null;
  const { data, error } = await supabase.from('accounts_period_locks').select('locked_through').eq('company', company).maybeSingle();
  if (error) { _lockOk = false; return null; }
  _lockOk = true;
  return data?.locked_through || null;
}
async function assertUnlocked(company, dates) {
  const lk = await getLockedThrough(company);
  if (!lk) return true;
  const bad = (dates || []).filter(Boolean).find(d => String(d) <= lk);
  if (bad) { toast.error(`${company} books are locked through ${lk}. An entry dated ${bad} cannot be added, changed or deleted.`); return false; }
  return true;
}
function addMonthsStr(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(y, m - 1 + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  return dateStrOf(new Date(t.getFullYear(), t.getMonth(), Math.min(d, last)));
}

const INR = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const curYear = new Date().getFullYear();
const curMonth = new Date().getMonth()+1;

// ── Main Component ─────────────────────────────────────────────────
// ── Company definitions — add new companies here in future ────────
const COMPANY_DEFS = [
  { key:'ARMSTECH',     label:'ARMSTECH',     color:'#0ea5e9', icon:'🏢',     pkey:'accounts_armstech' },
  { key:'SLCCS',        label:'SLCCS',        color:'#8b5cf6', icon:'🏛️', pkey:'accounts_slccs' },
  { key:'CONSTRUCTION', label:'Construction', color:'#f59e0b', icon:'🏗️', pkey:'accounts_construction' },
];


// ── EXPENSE INBOX ──────────────────────────────────────────────────────
const ENTRY_TYPES = [
  { key:'petty_cash',   label:'Petty Cash',    color:'#f59e0b', icon:'PC' },
  { key:'site_expense', label:'Site Expense',  color:'#3b82f6', icon:'SE' },
  { key:'reimbursement',label:'Reimbursement', color:'#22c55e', icon:'RE' },
];

function ExpenseInbox({ isEditor, company, user, projects, refreshKey=0 }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('submitted');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [dbCategoriesByType, setDbCategoriesByType] = useState({});
  const [dbPayModes, setDbPayModes] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selected, setSelected] = useState(new Set());          // bulk approve
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptOk, setReceiptOk] = useState(false);            // expense_entries.receipt_path exists
  const [notifyOk, setNotifyOk] = useState(false);              // notifications.staff_id exists
  const myStaffRef = React.useRef(null);
  useEffect(()=>{
    supabase.from('expense_entries').select('receipt_path').limit(1).then(({error})=>setReceiptOk(!error));
    supabase.from('notifications').select('staff_id').limit(1).then(({error})=>setNotifyOk(!error));
  },[]);
  async function myStaffId() {
    if (myStaffRef.current) return myStaffRef.current;
    const { data } = await supabase.from('staff').select('id').eq('user_id', user?.id).maybeSingle();
    myStaffRef.current = data?.id || null;
    return myStaffRef.current;
  }
  // Tell the submitter their expense was approved / sent back (needs notifications.staff_id; best effort)
  async function notifyStaff(entry, title, message) {
    if (!notifyOk || !entry.staff_id) return;
    try {
      await supabase.from('notifications').insert({ title, message, type: 'info', link: '/accounts', is_global: false, staff_id: String(entry.staff_id), created_by: await myStaffId() });
    } catch (e) { /* best effort */ }
  }
  async function openReceipt(path) {
    const { data, error } = await supabase.storage.from('expense-receipts').createSignedUrl(path, 300);
    if (error) { toast.error('Could not open the receipt: ' + error.message); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  }
  useEffect(()=>{
    supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setDbPayModes((data||[]).map(m=>m.name)));
    supabase.from('accounts_categories').select('name,category_type').order('name')
      .then(({data})=>{
        const byType = {};
        (data||[]).forEach(c=>{ if(!byType[c.category_type||'General']) byType[c.category_type||'General']=[]; byType[c.category_type||'General'].push(c.name); });
        setDbCategoriesByType(byType);
      });
  },[]);

  const isApprover = isApproverUser(user);

  const emptyEntry = {
    date: todayStr(),
    type: 'Cash Out', category: '', payment_mode: 'Cash',
    party_name: '', amount: '', reference: '', description: '',
    entry_type: 'site_expense', project_id: '', is_reimbursement: false,
    company,
  };
  const [form, setForm] = useState(emptyEntry);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => { loadEntries(); loadStaff(); }, [filterStatus, filterType, company, refreshKey]);

  async function loadEntries() {
    setLoading(true);
    let q = supabase.from('expense_entries')
      .select('*, staff:staff!staff_id(full_name, email)')
      .or(`company.eq.${company},company.is.null`)   // this company's entries (older entries without a company stay visible)
      .order('created_at', {ascending: false});
    if (filterStatus) q = q.eq('status', filterStatus);
    if (filterType) q = q.eq('entry_type', filterType);
    // Non-approvers see only their own
    if (!isApprover) {
      const { data: myStaff } = user ? await supabase.from('staff').select('id').eq('user_id', user.id).maybeSingle() : { data: null };
      if (!myStaff) { setEntries([]); setLoading(false); return; }   // never fall back to showing everyone's entries
      q = q.eq('staff_id', myStaff.id);
    }
    const { data, error } = await q;
    if (error) toast.error('Could not load expense entries: ' + error.message);
    setEntries(data || []);
    setLoading(false);
  }

  async function loadStaff() {
    const { data } = await supabase.from('staff').select('id,full_name,email').eq('is_active', true).order('full_name');
    setStaffList(data || []);
  }

  async function saveEntry() {
    if (!form.category || !form.amount || !form.date) { toast.error('Date, category and amount required'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Amount must be greater than zero'); return; }
    setSaving(true);
    try {
      let staff_id = editEntry?.staff_id;
      if (!staff_id) {
        const { data: myStaff } = await supabase.from('staff').select('id').eq('user_id', user?.id).maybeSingle();
        staff_id = myStaff?.id;
      }
      if (!staff_id) { toast.error('Your login is not linked to a staff record, so the entry cannot be saved.'); setSaving(false); return; }
      let receipt_path = editEntry?.receipt_path || null;
      if (receiptFile) {
        const safe = receiptFile.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
        const path = `${company}/${staff_id}/${Date.now()}_${safe}`;
        const { error: upErr } = await supabase.storage.from('expense-receipts').upload(path, receiptFile, { contentType: receiptFile.type || undefined });
        if (upErr) throw new Error('Receipt upload failed: ' + upErr.message);
        receipt_path = path;
      }
      // Whitelist the columns (the edit form is a copy of the joined row, which also carries `staff`, ids and timestamps)
      const resubmit = !!editEntry && editEntry.status === 'correction_needed';
      const payload = {
        date: form.date, type: form.type, category: form.category, payment_mode: form.payment_mode,
        party_name: form.party_name || null, amount: Number(form.amount), reference: form.reference || null,
        description: form.description || null, entry_type: form.entry_type,
        project_id: form.project_id || null, is_reimbursement: !!form.is_reimbursement,
        staff_id, company,
        status: isApprover ? (editEntry ? (resubmit ? 'submitted' : editEntry.status) : 'submitted') : 'submitted',
      };
      if (resubmit || (editEntry && !isApprover)) payload.rejection_reason = null;
      if (receiptOk && receipt_path) payload.receipt_path = receipt_path;
      const { error } = editEntry?.id
        ? await supabase.from('expense_entries').update(payload).eq('id', editEntry.id)
        : await supabase.from('expense_entries').insert(payload);
      if (error) throw error;
      toast.success(editEntry?.id ? 'Entry updated' : 'Expense submitted for review');
      setShowForm(false); setEditEntry(null); setForm(emptyEntry); setReceiptFile(null);
      loadEntries();
    } catch(e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  }

  async function approveEntry(entry, quiet = false) {
    if (entry.status !== 'submitted') { toast.error('This entry is no longer pending'); if (!quiet) loadEntries(); return false; }
    setSaving(true);
    if (!(await assertUnlocked(entry.company || company, [entry.date]))) { setSaving(false); return false; }
    let claimed = false;
    let ok = false;
    try {
      const { data: approverStaff } = await supabase.from('staff').select('id').eq('user_id', user?.id).maybeSingle();
      // 1. Claim the entry first (only succeeds while it is still 'submitted') so two approvers can never post it twice
      const { data: claimRows, error: claimErr } = await supabase.from('expense_entries').update({
        status: 'approved', approved_by: approverStaff?.id || null, approved_at: new Date().toISOString(),
      }).eq('id', entry.id).eq('status', 'submitted').select('id');
      if (claimErr) throw claimErr;
      if (!claimRows || claimRows.length === 0) { toast.error('Someone else already handled this entry'); if (!quiet) loadEntries(); setSaving(false); return false; }
      claimed = true;
      // 2. Post to the cash book
      const txn = {
        company: entry.company || company,
        date: entry.date,
        type: entry.type,
        category: entry.category,
        payment_mode: entry.payment_mode,
        payment_type: String(entry.payment_mode||'').toLowerCase()==='cash' ? 'Cash' : 'Online',
        party_name: entry.party_name,
        amount: entry.amount,
        reference: entry.reference,
        description: `[${ENTRY_TYPES.find(t=>t.key===entry.entry_type)?.label||'Expense'}] ${entry.description||''}`,
        source: 'expense_entry',
        project_id: entry.project_id || null,
        entry_by: entry.staff?.full_name || 'Staff',
      };
      const { data: posted, error: postErr } = await supabase.from('accounts_transactions').insert(txn).select('id').single();
      if (postErr) throw postErr;
      await supabase.from('expense_entries').update({ accounts_transaction_id: posted?.id }).eq('id', entry.id);
      claimed = false;
      // 3. Deduct from petty cash balance if petty_cash entry type
      if (entry.entry_type === 'petty_cash') {
        const { data: pcAcc } = await supabase.from('petty_cash_accounts')
          .select('*').eq('staff_id', entry.staff_id).maybeSingle();
        if (pcAcc) {
          if (Number(entry.amount) > Number(pcAcc.current_balance||0)) toast('Note: this expense is more than the staff member\'s petty cash balance', { icon: '⚠️' });
          const { error: pcErr } = await supabase.from('petty_cash_accounts').update({
            current_balance: Math.max(0, Number(pcAcc.current_balance||0) - Number(entry.amount)),
            total_spent: Number(pcAcc.total_spent||0) + Number(entry.amount),
            updated_at: new Date().toISOString(),
          }).eq('id', pcAcc.id);
          if (pcErr) toast.error('Posted, but petty cash balance was not updated: ' + pcErr.message);
        }
      }
      logAudit(user, 'approve', 'expense_entry', { id: entry.id, company: entry.company || company }, { summary: `Approved ${txnSummary(txn)} (by ${entry.staff?.full_name || 'staff'})`, after: txnSnap(txn) });
      notifyStaff(entry, '✅ Expense approved', `Your ${entry.category} expense of ₹${Number(entry.amount||0).toLocaleString('en-IN')} (${entry.date}) was approved and posted to accounts.`);
      ok = true;
      if (!quiet) { toast.success('Approved and posted to accounts!'); loadEntries(); }
    } catch(e) {
      if (claimed) await supabase.from('expense_entries').update({ status: 'submitted', approved_by: null, approved_at: null }).eq('id', entry.id);   // undo the claim
      toast.error('Approve failed: ' + e.message);
      if (!quiet) loadEntries();
    }
    setSaving(false);
    return ok;
  }

  async function bulkApprove() {
    const list = entries.filter(e => selected.has(e.id) && e.status === 'submitted');
    if (list.length === 0) return;
    if (!window.confirm(`Approve ${list.length} expense ${list.length === 1 ? 'entry' : 'entries'} and post ${list.length === 1 ? 'it' : 'them'} to the cash book?`)) return;
    let done = 0;
    for (const e of list) { if (await approveEntry(e, true)) done++; }
    setSelected(new Set());
    toast.success(`${done} of ${list.length} approved and posted`);
    loadEntries();
  }

  async function rejectEntry(entry, reason) {
    const { error } = await supabase.from('expense_entries').update({
      status: 'correction_needed',
      rejection_reason: reason,
    }).eq('id', entry.id).eq('status', 'submitted');
    if (error) { toast.error('Could not send back: ' + error.message); return; }
    logAudit(user, 'reject', 'expense_entry', { id: entry.id, company: entry.company || company }, { summary: `Sent back: ${entry.category} ₹${Number(entry.amount||0).toLocaleString('en-IN')} - ${reason}` });
    notifyStaff(entry, '↩️ Expense sent back', `Your ${entry.category} expense of ₹${Number(entry.amount||0).toLocaleString('en-IN')} (${entry.date}) needs correction: ${reason}`);
    toast.success('Sent back for correction');
    loadEntries();
  }

  const statusColors = {
    draft: 'badge-gray', submitted: 'badge-amber',
    under_review: 'badge-blue', approved: 'badge-green',
    rejected: 'badge-red', correction_needed: 'badge-amber',
  };

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:15}}>Expense Inbox</span>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{fontSize:12}}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted (Pending)</option>
          <option value="approved">Approved</option>
          <option value="correction_needed">Needs Correction</option>
        </select>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{fontSize:12}}>
          <option value="">All Types</option>
          {ENTRY_TYPES.map(t=><option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={()=>{setEditEntry(null);setReceiptFile(null);setForm(emptyEntry);setShowForm(true);}}>
          + New Expense Entry
        </button>
        <span style={{fontSize:12,color:'var(--text3)',marginLeft:'auto'}}>{entries.length} entries</span>
      </div>

      {isApprover && entries.some(e=>e.status==='submitted') && (
        <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
            <input type="checkbox"
              checked={entries.filter(e=>e.status==='submitted').every(e=>selected.has(e.id))}
              onChange={ev=>setSelected(ev.target.checked ? new Set(entries.filter(e=>e.status==='submitted').map(e=>e.id)) : new Set())}/>
            Select all pending
          </label>
          {selected.size>0 && <button className="btn btn-primary btn-sm" style={{background:'var(--green)',border:'none'}} disabled={saving} onClick={bulkApprove}>✓ Approve selected ({selected.size})</button>}
        </div>
      )}
      {loading ? <div className="loading"><div className="spinner"/></div> : (
        <div>
          {entries.length === 0 && <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No entries found</div>}
          {entries.map(e=>{
            const etype = ENTRY_TYPES.find(t=>t.key===e.entry_type);
            return (
              <div key={e.id} className="card" style={{marginBottom:10,border:`1px solid ${etype?.color}33`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                  <div>
                    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4}}>
                      {isApprover && e.status==='submitted' && <input type="checkbox" checked={selected.has(e.id)} onChange={()=>setSelected(p=>{const n=new Set(p);n.has(e.id)?n.delete(e.id):n.add(e.id);return n;})}/>}
                      <span style={{fontSize:16}}>{etype?.icon}</span>
                      <span style={{fontWeight:700,fontSize:13}}>{e.category}</span>
                      <span className={`badge ${statusColors[e.status]||'badge-gray'}`} style={{fontSize:10}}>{e.status}</span>
                      <span style={{padding:'2px 8px',borderRadius:8,fontSize:10,fontWeight:600,background:etype?.color+'22',color:etype?.color}}>{etype?.label}</span>
                    </div>
                    <div style={{fontSize:12,color:'var(--text3)'}}>
                      {fmtDMY(e.date)} · {e.payment_mode} · {e.staff?.full_name||'Unknown'}
                      {e.party_name && <> · {e.party_name}</>}
                    </div>
                    {e.description && <div style={{fontSize:12,marginTop:4,color:'var(--text2)'}}>{e.description}</div>}
                    {e.rejection_reason && <div style={{fontSize:11,color:'var(--red)',marginTop:4}}>⚠️ {e.rejection_reason}</div>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
                    <span style={{fontWeight:800,fontSize:16,color:e.type==='Cash Out'?'var(--red)':'var(--green)'}}>
                      {e.type==='Cash Out'?'-':'+'}₹{Number(e.amount).toLocaleString('en-IN')}
                    </span>
                    <div style={{display:'flex',gap:4}}>
                      {e.receipt_path && <button className="btn btn-ghost btn-sm" title="View receipt" onClick={()=>openReceipt(e.receipt_path)}>📎 Receipt</button>}
                      {e.status!=='approved' && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditEntry(e);setReceiptFile(null);setForm({...emptyEntry,...e,amount:String(e.amount??''),project_id:e.project_id||''});setShowForm(true);}}>Edit</button>}
                      {isApprover && e.status==='submitted' && (
                        <>
                          <button className="btn btn-primary btn-sm" style={{background:'var(--green)',border:'none'}}
                            onClick={()=>approveEntry(e)} disabled={saving}>✓ Approve</button>
                          <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}}
                            onClick={()=>{setRejectReason('');setRejectFor(e);}}>Send back</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rejectFor && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setRejectFor(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header"><span className="modal-title">Send back for correction</span><button className="btn btn-ghost btn-sm" onClick={()=>setRejectFor(null)}>×</button></div>
            <div className="modal-body">
              <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>{rejectFor.category} · ₹{Number(rejectFor.amount||0).toLocaleString('en-IN')} · {rejectFor.staff?.full_name||'Unknown'}</div>
              <div className="form-group"><label>What needs to be corrected? *</label>
                <textarea rows={3} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="e.g. Receipt number missing, wrong category"/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setRejectFor(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!rejectReason.trim()} onClick={async()=>{await rejectEntry(rejectFor,rejectReason.trim());setRejectFor(null);}}>Send back</button>
            </div>
          </div>
        </div>
      )}

      {/* Entry Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-header">
              <span className="modal-title">{editEntry?'Edit':'New'} Expense Entry</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Entry Type *</label>
                  <select value={form.entry_type} onChange={e=>sf('entry_type',e.target.value)}>
                    {ENTRY_TYPES.map(t=><option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <DateInput value={form.date} onChange={e=>sf('date',e.target.value)} max={todayStr()}/>
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select value={form.type} onChange={e=>sf('type',e.target.value)}>
                    {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select value={form.category} onChange={e=>sf('category',e.target.value)}>
                    <option value="">Select Category</option>
                    {Object.keys(dbCategoriesByType).sort().map(type=>(
                      <optgroup key={type} label={type}>
                        {dbCategoriesByType[type].map(cat=><option key={cat} value={cat}>{cat}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e=>sf('payment_mode',e.target.value)}>
                    {dbPayModes.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount (&#8377;) *</label>
                  <input type="number" value={form.amount} onChange={e=>sf('amount',e.target.value)} min={0} placeholder="0.00"/>
                </div>
                <div className="form-group">
                  <label>Party Name</label>
                  <input value={form.party_name||''} onChange={e=>sf('party_name',e.target.value)} placeholder="Vendor/Party"/>
                </div>
                <div className="form-group">
                  <label>Reference / Receipt No</label>
                  <input value={form.reference||''} onChange={e=>sf('reference',e.target.value)} placeholder="Receipt no"/>
                </div>
                {projects.length>0 && (
                  <div className="form-group">
                    <label>Project (if applicable)</label>
                    <select value={form.project_id||''} onChange={e=>sf('project_id',e.target.value)}>
                      <option value="">No Project</option>
                      {projects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Description</label>
                  <textarea value={form.description||''} onChange={e=>sf('description',e.target.value)} rows={2} placeholder="Details of expense..."/>
                </div>
                {receiptOk && (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Receipt / bill (photo or PDF, max 5 MB)</label>
                    <input type="file" accept="image/*,application/pdf" onChange={e=>{
                      const fl = e.target.files?.[0] || null;
                      if (fl && fl.size > 5*1024*1024) { toast.error('File is larger than 5 MB'); e.target.value=''; setReceiptFile(null); return; }
                      setReceiptFile(fl);
                    }}/>
                    {editEntry?.receipt_path && !receiptFile && <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>A receipt is already attached — choose a file only to replace it.</div>}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEntry} disabled={saving}>
                {saving?'Saving...':editEntry?'Update':'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PETTY CASH ──────────────────────────────────────────────────────────
function PettyCash({ isEditor, company, user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [pcPayModes, setPcPayModes] = useState([]);
  const [issueForm, setIssueForm] = useState({staff_id:'',amount:'',issue_date:todayStr(),payment_mode:'',reference:'',notes:''});
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving] = useState(false);

  const isApprover = isApproverUser(user);

  useEffect(() => { loadAccounts(); loadStaff(); supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setPcPayModes((data||[]).map(m=>m.name))); }, []);


  async function loadAccounts() {
    setLoading(true);
    const { data } = await supabase.from('petty_cash_accounts')
      .select('*, staff(full_name, email, phone)')
      .order('updated_at', {ascending: false});
    setAccounts(data || []);
    setLoading(false);
  }

  async function loadStaff() {
    const { data } = await supabase.from('staff').select('id,full_name,email').eq('is_active', true).order('full_name');
    setStaffList(data || []);
  }

  async function issueAmount() {
    if (!issueForm.staff_id || !issueForm.amount) { toast.error('Staff and amount required'); return; }
    if (!(Number(issueForm.amount) > 0)) { toast.error('Amount must be greater than zero'); return; }
    setSaving(true);
    let issueId = null;
    try {
      const amount = Number(issueForm.amount);
      // 1. Record the issuance first (it is the audit trail); undone below if the balance update fails
      const { data: issueRow, error: issueErr } = await supabase.from('petty_cash_issues').insert({
        staff_id: issueForm.staff_id,
        amount,
        issue_date: issueForm.issue_date,
        payment_mode: issueForm.payment_mode || pcPayModes[0] || 'Cash',
        reference: issueForm.reference||null,
        notes: issueForm.notes||null,
      }).select('id').single();
      if (issueErr) throw new Error('Issue record: ' + issueErr.message);
      issueId = issueRow?.id;
      // 2. Update (or create) the staff member's petty cash account
      const { data: existing, error: fetchErr } = await supabase.from('petty_cash_accounts')
        .select('*').eq('staff_id', issueForm.staff_id).maybeSingle();
      if (fetchErr) throw new Error('Fetch: ' + fetchErr.message);
      const { error: accErr } = existing
        ? await supabase.from('petty_cash_accounts').update({
            current_balance: Number(existing.current_balance||0) + amount,
            total_issued: Number(existing.total_issued||0) + amount,
            updated_at: new Date().toISOString(),
          }).eq('id', existing.id)
        : await supabase.from('petty_cash_accounts').insert({
            staff_id: issueForm.staff_id, current_balance: amount, total_issued: amount, total_spent: 0,
          });
      if (accErr) throw new Error('Account: ' + accErr.message);
      issueId = null;   // committed
      logAudit(user, 'issue', 'petty_cash', { company }, { summary: `Issued ₹${amount.toLocaleString('en-IN')} petty cash to ${staffList.find(s=>s.id===issueForm.staff_id)?.full_name||'staff'}` });
      toast.success('Petty cash issued successfully! Expenses will be posted to accounts when approved.');
      setShowIssueForm(false);
      setIssueForm({staff_id:'',amount:'',issue_date:todayStr(),payment_mode:'',reference:'',notes:''});
      loadAccounts();
    } catch(e) {
      if (issueId) await supabase.from('petty_cash_issues').delete().eq('id', issueId);   // roll back the half-done issue
      toast.error('Issue failed: ' + e.message);
    }
    setSaving(false);
  }

  // Staff hands unused petty cash back
  const [settleFor, setSettleFor] = useState(null);
  const [settleForm, setSettleForm] = useState({ amount: '', payment_mode: 'Cash', notes: '' });
  async function settle() {
    const amount = Number(settleForm.amount);
    if (!(amount > 0)) { toast.error('Enter the amount returned'); return; }
    setSaving(true);
    try {
      const { data: acc, error: aErr } = await supabase.from('petty_cash_accounts').select('*').eq('id', settleFor.id).single();   // fresh balance
      if (aErr) throw aErr;
      if (amount > Number(acc.current_balance || 0)) { toast.error(`Only ${INR(acc.current_balance)} is outstanding`); setSaving(false); return; }
      const today = todayStr();
      if (POST_SETTLEMENT_TO_CASHBOOK && !(await assertUnlocked(company, [today]))) { setSaving(false); return; }
      const { data: rec, error: rErr } = await supabase.from('petty_cash_issues').insert({
        staff_id: acc.staff_id, amount: -amount, issue_date: today, payment_mode: settleForm.payment_mode || 'Cash',
        notes: settleForm.notes ? `Settlement - ${settleForm.notes}` : 'Settlement - cash returned',
      }).select('id').single();
      if (rErr) throw rErr;
      const { error: uErr } = await supabase.from('petty_cash_accounts').update({
        current_balance: Number(acc.current_balance) - amount, updated_at: new Date().toISOString(),
      }).eq('id', acc.id);
      if (uErr) { await supabase.from('petty_cash_issues').delete().eq('id', rec?.id); throw uErr; }
      if (POST_SETTLEMENT_TO_CASHBOOK) {
        const { error: tErr } = await supabase.from('accounts_transactions').insert({
          company, date: today, type: 'Cash In', category: 'Petty Cash Settlement',
          payment_mode: settleForm.payment_mode || 'Cash', amount,
          description: `Petty cash settled by ${settleFor.staff?.full_name}`,
          source: 'petty_cash', entry_by: user?.email || 'Admin',
        });
        if (tErr) toast.error('Settlement saved, but it could not be posted to the cash book: ' + tErr.message);
      }
      logAudit(user, 'settle', 'petty_cash', { company }, { summary: `${settleFor.staff?.full_name} returned ${INR(amount)} petty cash` });
      toast.success('Settlement recorded!');
      setSettleFor(null);
      loadAccounts();
    } catch (e) { toast.error('Settlement failed: ' + e.message); }
    setSaving(false);
  }

  const [selStaffHistory, setSelStaffHistory] = useState(null);
  const [history, setHistory] = useState([]);

  async function loadHistory(staffId) {
    const { data } = await supabase.from('petty_cash_issues')
      .select('*').eq('staff_id', staffId).order('issue_date', {ascending:false});
    setHistory(data || []);
    setSelStaffHistory(staffId);
  }

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center'}}>
        <span style={{fontWeight:700,fontSize:15}}>Petty Cash Management</span>
        {isApprover && <button className="btn btn-primary btn-sm" onClick={()=>setShowIssueForm(true)}>+ Issue Amount</button>}
      </div>

      {loading ? <div className="loading"><div className="spinner"/></div> : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
          {accounts.length===0 && <div style={{color:'var(--text3)',fontSize:13}}>No petty cash accounts yet</div>}
          {accounts.map(acc=>(
            <div key={acc.id} className="card" style={{border:'1px solid var(--border)'}}>
              <div style={{fontWeight:700,fontSize:14,marginBottom:8}}>{acc.staff?.full_name}</div>
              <div style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>{acc.staff?.email}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,textAlign:'center'}}>
                <div style={{background:'var(--green-light)',borderRadius:6,padding:8}}>
                  <div style={{fontWeight:700,color:'var(--green)',fontSize:15}}>₹{Number(acc.current_balance||0).toLocaleString('en-IN')}</div>
                  <div style={{fontSize:10,color:'var(--text3)'}}>Balance</div>
                </div>
                <div style={{background:'var(--accent-light)',borderRadius:6,padding:8}}>
                  <div style={{fontWeight:700,color:'var(--accent)',fontSize:15}}>₹{Number(acc.total_issued||0).toLocaleString('en-IN')}</div>
                  <div style={{fontSize:10,color:'var(--text3)'}}>Issued</div>
                </div>
                <div style={{background:'var(--red-light)',borderRadius:6,padding:8}}>
                  <div style={{fontWeight:700,color:'var(--red)',fontSize:15}}>₹{Number(acc.total_spent||0).toLocaleString('en-IN')}</div>
                  <div style={{fontSize:10,color:'var(--text3)'}}>Spent</div>
                </div>
              </div>
              {(() => { const returned = Math.max(0, Number(acc.total_issued||0) - Number(acc.total_spent||0) - Number(acc.current_balance||0)); return returned > 0.004 ? <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>Returned to office: <strong>{INR(returned)}</strong></div> : null; })()}
              <div style={{display:'flex',gap:6,marginTop:10}}>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                  onClick={()=>loadHistory(acc.staff_id)}>View History</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History Modal */}
      {selStaffHistory && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSelStaffHistory(null)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <span className="modal-title">Petty Cash History — {accounts.find(a=>a.staff_id===selStaffHistory)?.staff?.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelStaffHistory(null)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'var(--accent-light)'}}>
                    <th style={{padding:'6px 10px',textAlign:'left'}}>Date</th>
                    <th style={{padding:'6px 10px',textAlign:'right'}}>Amount</th>
                    <th style={{padding:'6px 10px',textAlign:'left'}}>Mode</th>
                    <th style={{padding:'6px 10px',textAlign:'left'}}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h,i)=>(
                    <tr key={h.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                      <td style={{padding:'6px 10px'}}>{fmtDMY(h.issue_date)}</td>
                      <td style={{padding:'6px 10px',textAlign:'right',fontWeight:600,
                        color:Number(h.amount)<0?'var(--green)':'var(--accent)'}}>
                        {Number(h.amount)<0?'+':''}{INR(Math.abs(Number(h.amount)))}
                        {Number(h.amount)<0&&<span style={{fontSize:10,color:'var(--green)',marginLeft:4}}>Settled</span>}
                      </td>
                      <td style={{padding:'6px 10px'}}>{h.payment_mode||'-'}</td>
                      <td style={{padding:'6px 10px',color:'var(--text3)'}}>{h.notes||'-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setSelStaffHistory(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Section */}
      {accounts.length > 0 && isApprover && (
        <div style={{marginTop:20}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>Settlement</div>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
            When a staff member returns unused petty cash, record it here.
          </div>
          {accounts.filter(a=>Number(a.current_balance)>0).map(acc=>(
            <div key={acc.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',
              background:'var(--surface2)',borderRadius:8,marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:13}}>{acc.staff?.full_name}</div>
                <div style={{fontSize:11,color:'var(--text3)'}}>Balance: {INR(acc.current_balance)}</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={()=>{setSettleForm({amount:'',payment_mode:'Cash',notes:''});setSettleFor(acc);}}>Settle</button>
            </div>
          ))}
        </div>
      )}

      {settleFor && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSettleFor(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header"><span className="modal-title">Settle Petty Cash — {settleFor.staff?.full_name}</span><button className="btn btn-ghost btn-sm" onClick={()=>setSettleFor(null)}>×</button></div>
            <div className="modal-body"><div className="form-grid">
              <div className="form-group" style={{gridColumn:'1/-1'}}><div style={{fontSize:12,color:'var(--text3)'}}>Outstanding balance: <strong>{INR(settleFor.current_balance)}</strong></div></div>
              <div className="form-group"><label>Amount returned (₹) *</label><input type="number" min="0" value={settleForm.amount} onChange={e=>setSettleForm(f=>({...f,amount:e.target.value}))}/></div>
              <div className="form-group"><label>Received into</label>
                <select value={settleForm.payment_mode} onChange={e=>setSettleForm(f=>({...f,payment_mode:e.target.value}))}>
                  {(pcPayModes.length?pcPayModes:['Cash']).map(m=><option key={m} value={m}>{m}</option>)}
                </select></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label>Notes</label><input value={settleForm.notes} onChange={e=>setSettleForm(f=>({...f,notes:e.target.value}))}/></div>
            </div></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setSettleFor(null)}>Cancel</button><button className="btn btn-primary" onClick={settle} disabled={saving}>{saving?'Saving...':'Record Settlement'}</button></div>
          </div>
        </div>
      )}

      {/* Issue Form Modal */}
      {showIssueForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowIssueForm(false)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header">
              <span className="modal-title">Issue Petty Cash</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowIssueForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Staff Member *</label>
                  <select value={issueForm.staff_id} onChange={e=>setIssueForm(f=>({...f,staff_id:e.target.value}))}>
                    <option value="">Select Staff</option>
                    {staffList.map(s=><option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount (&#8377;) *</label>
                  <input type="number" value={issueForm.amount} onChange={e=>setIssueForm(f=>({...f,amount:e.target.value}))} min={0} placeholder="0.00"/>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <DateInput value={issueForm.issue_date} onChange={e=>setIssueForm(f=>({...f,issue_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select value={issueForm.payment_mode||pcPayModes[0]||''} onChange={e=>setIssueForm(f=>({...f,payment_mode:e.target.value}))}>
                    {pcPayModes.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference</label>
                  <input value={issueForm.reference} onChange={e=>setIssueForm(f=>({...f,reference:e.target.value}))} placeholder="Receipt/transfer ref"/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <textarea value={issueForm.notes} onChange={e=>setIssueForm(f=>({...f,notes:e.target.value}))} rows={2}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowIssueForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={issueAmount} disabled={saving}>
                {saving?'Issuing...':'Issue Amount'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── MANAGE CATEGORIES ────────────────────────────────────────────────────────
const CAT_TYPES = ['Training','Construction','Finance','General'];

function ManageCategories({ isEditor }) {
  const { canEdit } = useAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name:'', type:'both', category_type:'General' };
  const [form, setForm] = useState(emptyForm);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{ load(); },[]);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('accounts_categories').select('*').order('category_type').order('name');
    setCats(data||[]);
    setLoading(false);
  }

  async function save() {
    if (!form.name) { toast.error('Category name required'); return; }
    setSaving(true);
    if (editing) {
      const renamed = editing.name !== form.name;
      let cascade = false;
      if (renamed) {
        const { count } = await supabase.from('accounts_transactions').select('id', { count: 'exact', head: true }).eq('category', editing.name);
        if ((count || 0) > 0) {
          if (!window.confirm(`${count} existing entries use "${editing.name}". Rename them to "${form.name}" as well?\n\nOK = rename everywhere, Cancel = stop.`)) { setSaving(false); return; }
          cascade = true;
        }
      }
      const { error } = await supabase.from('accounts_categories').update({
        name: form.name, type: form.type, category_type: form.category_type
      }).eq('id', editing.id);
      if (error) toast.error(error.message);
      else {
        if (cascade) {
          const { error: e2 } = await supabase.from('accounts_transactions').update({ category: form.name }).eq('category', editing.name);
          if (e2) toast.error('Category renamed, but existing entries could not be updated: ' + e2.message);
        }
        toast.success('Category updated'); setShowForm(false); setEditing(null); load();
      }
    } else {
      const { error } = await supabase.from('accounts_categories').insert({
        name: form.name, type: form.type, category_type: form.category_type, company:'ALL'
      });
      if (error) toast.error(error.message);
      else { toast.success('Category added'); setShowForm(false); load(); }
    }
    setSaving(false);
  }

  async function del(cat) {
    const { count } = await supabase.from('accounts_transactions').select('id', { count: 'exact', head: true }).eq('category', cat.name);
    if ((count || 0) > 0) { toast.error(`"${cat.name}" is used by ${count} entries. Rename it or re-categorise those entries first.`); return; }
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    const { error } = await supabase.from('accounts_categories').delete().eq('id', cat.id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); load(); }
  }

  const filtered = cats.filter(c =>
    (!search || c.name.toLowerCase().includes(search.toLowerCase())) &&
    (!filterType || c.category_type === filterType)
  );

  const typeColors = { Training:'#3b82f6', Construction:'#f59e0b', Finance:'#8b5cf6', General:'#6b7280' };

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:15}}>Categories</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..."
          style={{padding:'5px 10px',borderRadius:6,border:'1px solid var(--border)',fontSize:12,width:160}}/>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{fontSize:12}}>
          <option value="">All Types</option>
          {CAT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} categories</span>
        {canEdit('accounts') && <button className="btn btn-primary btn-sm" style={{marginLeft:'auto'}}
          onClick={()=>{setEditing(null);setForm(emptyForm);setShowForm(true);}}>+ Add Category</button>}
      </div>

      {loading ? <div className="loading"><div className="spinner"/></div> : (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--accent-light)'}}>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Category Name</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Type</th>
                <th style={{padding:'8px 12px',textAlign:'left'}}>Direction</th>
                {canEdit('accounts') && <th style={{padding:'8px 12px',textAlign:'left'}}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((cat,i)=>(
                <tr key={cat.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                  <td style={{padding:'8px 12px',fontWeight:500}}>{cat.name}</td>
                  <td style={{padding:'8px 12px'}}>
                    <span style={{padding:'2px 10px',borderRadius:10,fontSize:11,fontWeight:600,
                      background:(typeColors[cat.category_type]||'#6b7280')+'22',
                      color:typeColors[cat.category_type]||'#6b7280'}}>
                      {cat.category_type||'General'}
                    </span>
                  </td>
                  <td style={{padding:'8px 12px'}}>
                    <span style={{fontSize:11,color:'var(--text3)'}}>{cat.type}</span>
                  </td>
                  {canEdit('accounts') && (
                    <td style={{padding:'8px 12px'}}>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-ghost btn-sm"
                          onClick={()=>{setEditing(cat);setForm({name:cat.name,type:cat.type,category_type:cat.category_type||'General'});setShowForm(true);}}>
                          Edit
                        </button>
                        <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}}
                          onClick={()=>del(cat)}>Del</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Category</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Category Name *</label>
                  <input value={form.name} onChange={e=>sf('name',e.target.value)} placeholder="e.g. Site Materials"/>
                </div>
                <div className="form-group">
                  <label>Category Type</label>
                  <select value={form.category_type} onChange={e=>sf('category_type',e.target.value)}>
                    {CAT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Direction</label>
                  <select value={form.type} onChange={e=>sf('type',e.target.value)}>
                    <option value="in">Cash In only</option>
                    <option value="out">Cash Out only</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':editing?'Update':'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ACC-NOREF-ENTRYBY
export default function Accounts() {
  const [company, setCompany] = useState('ARMSTECH');
  const [tab,     setTab]     = useState('cashbook');
  const [projects, setProjects] = useState([]);
  const [showAddProject, setShowAddProject] = useState(false);
  const [projectForm, setProjectForm] = useState({project_name:'',project_code:'',location:'',client_name:'',start_date:'',end_date:''});
  const [selectedProject, setSelectedProject] = useState(null);
  const [staffProjectPerms, setStaffProjectPerms] = useState([]);
  const { isEditor, canEdit, canView, user } = useAuth();

  useEffect(() => {
    loadProjects();
  }, []);

  // Real-time updates (debounced): bump refreshKey so the open tab reloads its data
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => setRefreshKey(k => k + 1), 800); };
    const ch = supabase.channel('accounts-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'accounts_transactions'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'expense_entries'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function loadProjects() {
    const { data } = await supabase.from('construction_projects').select('*').eq('status','active').order('project_name');
    setProjects(data || []);
    // Load staff project permissions
    const { data: perms } = await supabase.from('staff_project_permissions')
      .select('*, staff(full_name, email, user_id)')
      .order('created_at', {ascending: false});
    setStaffProjectPerms(perms || []);
  }

  // Get projects this user can access
  function getUserProjects() {
    if (!user) return [];
    // Approvers/admins see all projects
    if (isApproverUser(user)) return projects;
    // Other staff see only assigned projects
    const myPerms = staffProjectPerms.filter(p => p.staff?.user_id === user.id);
    const myProjectIds = myPerms.map(p => p.project_id);
    return projects.filter(p => myProjectIds.includes(p.id));
  }

  function canViewArmstech() {
    if (!user) return false;
    if (user.email === 'ershadarmstech@gmail.com' || user.email === 'armstechac@gmail.com') return true;
    return staffProjectPerms.some(p => p.staff?.user_id === user.id && p.can_view_armstech);
  }

  function canViewSLCCS() {
    if (!user) return false;
    if (user.email === 'ershadarmstech@gmail.com' || user.email === 'armstechac@gmail.com') return true;
    return staffProjectPerms.some(p => p.staff?.user_id === user.id && p.can_view_slccs);
  }

  const innerTabs = [
    { key:'cashbook',       label:'📒 Cash Book',            pkey:'accounts_cashbook' },
    { key:'summary',        label:'📊 Monthly Summary',      pkey:'accounts_summary' },
    { key:'balances',       label:'🏦 Account Balances',     pkey:'accounts_balances' },
    { key:'entries',        label:'📝 All Entries',           pkey:'accounts_entries' },
    { key:'expense_inbox',  label:'📥 Expense Inbox',        pkey:'expense_inbox' },
    { key:'petty_cash',     label:'💰 Petty Cash',           pkey:'petty_cash' },
    { key:'report',         label:'📊 Financial Report',     pkey:'accounts_report' },
    { key:'party_ledger',   label:'👤 Party Ledger',         pkey:'accounts_party_ledger' },
    { key:'reminders',      label:'🔔 Reminders & Payments', pkey:'accounts_reminders' },
    { key:'reconciliation', label:'🏦 Reconciliation',       pkey:'accounts_reconciliation' },
    { key:'categories',     label:'🗂 Categories',            adminOnly:true },
    { key:'audit',          label:'🛡 Audit & Lock',          adminOnly:true },
  ].filter(t => t.adminOnly ? isApproverUser(user) : canView(t.pkey));

  // If the open tab is not one this user may see (e.g. after switching company or no cash-book permission), move to the first allowed one
  useEffect(() => {
    if (innerTabs.length && !innerTabs.some(t => t.key === tab)) setTab(innerTabs[0].key);
  }, [innerTabs.map(t => t.key).join('|')]);
  const myProjects = getUserProjects();

  const cDef = COMPANY_DEFS.find(c=>c.key===company) || COMPANY_DEFS[0];

  const visibleCompanies = COMPANY_DEFS.filter(c => canView(c.pkey));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Accounts</h1>
          <p className="page-subtitle">Cash book, expenses and income — select company below</p>
        </div>
      </div>

      {/* Company selector tabs */}
      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        {visibleCompanies.map(co=>(
          <button key={co.key} onClick={()=>{setCompany(co.key);setTab('cashbook');}}
            style={{
              padding:'10px 24px', borderRadius:10, border:'none', cursor:'pointer',
              fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8,
              background: company===co.key ? co.color : 'var(--bg3)',
              color: company===co.key ? '#fff' : 'var(--text2)',
              boxShadow: company===co.key ? `0 4px 14px ${co.color}44` : 'none',
              transition:'all 0.2s',
            }}>
            <span>{co.icon}</span>{co.label}
            {company===co.key && <span style={{background:'rgba(255,255,255,0.25)',borderRadius:4,padding:'1px 8px',fontSize:11}}>Active</span>}
          </button>
        ))}
      </div>

      {/* Inner tabs for selected company */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'12px 12px 0 0',padding:'0 4px'}}>
      {company==='CONSTRUCTION'&&(
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,padding:'12px 16px',background:'#fef3c7',borderRadius:8,border:'1px solid #f59e0b'}}>
          <span style={{fontSize:13,fontWeight:600,color:'#92400e'}}>🏗️ Project:</span>
          <select value={selectedProject?.id||''} onChange={e=>{const p=myProjects.find(x=>x.id===e.target.value);setSelectedProject(p||null);}} style={{flex:1,maxWidth:400}}>
            <option value="">— Select a Project —</option>
            {myProjects.map(p=><option key={p.id} value={p.id}>{p.project_code?`[${p.project_code}] `:''}{p.project_name}{p.location?` — ${p.location}`:''}</option>)}
          </select>
          {canEdit('accounts')&&<button className="btn btn-primary btn-sm" onClick={()=>setShowAddProject(true)}>+ New Project</button>}
        </div>
      )}
        <div style={{display:'flex',gap:0,borderBottom:'2px solid var(--border)'}}>
          {innerTabs.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{
                padding:'10px 20px', border:'none', cursor:'pointer', fontWeight:600,
                fontSize:13, background:'transparent',
                borderBottom: tab===t.key ? `2.5px solid ${cDef.color}` : '2.5px solid transparent',
                color: tab===t.key ? cDef.color : 'var(--text3)',
                marginBottom:-2,
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderTop:'none',borderRadius:'0 0 12px 12px',padding:20}}>
        {tab==='cashbook' && <CashBook    isEditor={isEditor} company={company} accentColor={cDef.color} projectId={selectedProject?.id} refreshKey={refreshKey}/>}
        {tab==='summary'  && <MonthlySummary               company={company} accentColor={cDef.color} projectId={selectedProject?.id}/>}
        {tab==='balances' && <AccountBalances               company={company} accentColor={cDef.color} projectId={selectedProject?.id}/>}
        {tab==='entries'  && <AllEntries  isEditor={isEditor} company={company} accentColor={cDef.color} projectId={selectedProject?.id} refreshKey={refreshKey}/>}
        {tab==='expense_inbox' && <ExpenseInbox isEditor={isEditor} company={company} user={user} projects={myProjects} refreshKey={refreshKey}/>}
        {tab==='petty_cash'    && <PettyCash    isEditor={isEditor} company={company} user={user}/>}
        {tab==='report'        && <FinancialReport company={company} projectId={selectedProject?.id}/>}
        {tab==='party_ledger'  && <PartyLedger company={company} projectId={selectedProject?.id}/>}
        {tab==='reminders'     && <PaymentReminders isEditor={isEditor} company={company} user={user}/>}
        {tab==='reconciliation'&& <BankReconciliation isEditor={isEditor} company={company}/>}
        {tab==='categories'    && <ManageCategories isEditor={isEditor}/>}
        {tab==='audit'         && <AuditAndLocks company={company} user={user}/>}
      </div>

      {/* ── Add Project Modal ── */}
      {showAddProject && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAddProject(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>🏗️ New Construction Project</h3>
              <button className="modal-close" onClick={()=>setShowAddProject(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Project Name *</label><input value={projectForm.project_name} onChange={e=>setProjectForm(f=>({...f,project_name:e.target.value}))} placeholder="e.g. SLCCS Admin Block"/></div>
                <div className="form-group"><label>Project Code</label><input value={projectForm.project_code} onChange={e=>setProjectForm(f=>({...f,project_code:e.target.value}))} placeholder="e.g. PROJ-001"/></div>
                <div className="form-group"><label>Location</label><input value={projectForm.location} onChange={e=>setProjectForm(f=>({...f,location:e.target.value}))} placeholder="e.g. Kochi"/></div>
                <div className="form-group"><label>Client Name</label><input value={projectForm.client_name} onChange={e=>setProjectForm(f=>({...f,client_name:e.target.value}))} placeholder="Client"/></div>
                <div className="form-group"><label>Start Date</label><DateInput value={projectForm.start_date} onChange={e=>setProjectForm(f=>({...f,start_date:e.target.value}))}/></div>
                <div className="form-group"><label>End Date</label><DateInput value={projectForm.end_date} onChange={e=>setProjectForm(f=>({...f,end_date:e.target.value}))}/></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowAddProject(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                if (!projectForm.project_name) { alert('Project name required'); return; }
                const { error } = await supabase.from('construction_projects').insert({...projectForm, status:'active'});
                if (error) alert(error.message);
                else { setShowAddProject(false); setProjectForm({project_name:'',project_code:'',location:'',client_name:'',start_date:'',end_date:''}); loadProjects(); }
              }}>Save Project</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared hook to load transactions ──────────────────────────────
function useTxns(filters={}) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqRef = React.useRef(0);

  const filtersKey = JSON.stringify(filters);
  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true);
    try {
      const all = await fetchAllRows(() => {
        let q = supabase.from('accounts_transactions')
          .select('*')
          .order('date',{ascending:false})
          .order('created_at',{ascending:false})
          .order('id',{ascending:false});
        if (filters.company)   q = q.eq('company', filters.company);
        if (filters.project_id) q = q.eq('project_id', filters.project_id);
        if (filters.payMode)   q = q.eq('payment_mode', filters.payMode);
        if (filters.type)      q = q.eq('type', filters.type);
        if (filters.category)  q = q.eq('category', filters.category);
        if (filters.fromDate)  q = q.gte('date', filters.fromDate);
        if (filters.toDate)    q = q.lte('date', filters.toDate);
        return q;
      });
      if (myReq === reqRef.current) setTxns(all);   // ignore stale responses
    } catch (e) {
      if (myReq === reqRef.current) toast.error('Could not load transactions: ' + e.message);
    }
    if (myReq === reqRef.current) setLoading(false);
  }, [filtersKey]);

  useEffect(()=>{ load(); },[load]);
  return { txns, setTxns, loading, reload: load };
}

// ─────────────────────────────────────────────────────────────────
// ENTRY MODAL — shared across tabs
// ─────────────────────────────────────────────────────────────────
const emptyForm = {
  company:'ARMSTECH', date: todayStr(),
  type:'Cash In', category:'', payment_mode:'', payment_type:'Cash', // PAYMENT-TYPE
  amount:'', reference:'', description:'', party_name:'', source:'manual', entry_by:'',
};

function EntryModal({ onClose, onSaved, editing, company='ARMSTECH', projectId=null }) {
  const { user } = useAuth();
  const [form, setForm] = useState(editing ? {...editing, amount:String(editing.amount||'')} : {...emptyForm, company});
  const [saving, setSaving] = useState(false);
  const [dbCats, setDbCats] = useState([]);
  const [dbParties, setDbParties] = useState([]);
  const [dbPayModes, setDbPayModes] = useState([]);
  const [entryByStaff, setEntryByStaff] = useState([]);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    supabase.from('accounts_categories').select('*').order('name').then(({data})=>setDbCats(data||[]));
    supabase.from('accounts_parties').select('*').order('party_name').then(({data})=>setDbParties(data||[]));
    supabase.from('accounts_payment_modes').select('*').order('name').then(({data})=>setDbPayModes(data||[]));
    (async()=>{
      let { data } = await supabase.from('staff').select('full_name, staff_category').in('staff_category',['office_admin','director']).eq('is_active',true).order('full_name');
      if (!data || data.length===0) {
        const r = await supabase.from('staff').select('full_name, staff_category').eq('is_active',true).order('full_name');
        data = r.data || [];
      }
      setEntryByStaff(data);
      // Auto-fill Entry By: map a stored email to the staff name, or default blank to the logged-in staff member
      try {
        const cur = (editing && editing.entry_by) || '';
        if (cur && cur.includes('@')) {
          const { data: m } = await supabase.from('staff').select('full_name').ilike('email', cur).maybeSingle();
          if (m?.full_name) setForm(f=>({...f, entry_by: m.full_name}));
        } else if (!cur && editing && editing.source === 'fees' && editing.reference) {
          // fee entries: use whoever recorded the receipt
          const { data: p } = await supabase.from('fee_payments').select('received_by').eq('receipt_number', editing.reference).limit(1).maybeSingle();
          if (p?.received_by) {
            const { data: st } = await supabase.from('staff').select('full_name').eq('id', p.received_by).maybeSingle();
            if (st?.full_name) setForm(f=>({...f, entry_by: st.full_name}));
          }
        } else if (!cur && !editing && user?.id) {
          const { data: me } = await supabase.from('staff').select('full_name').eq('user_id', user.id).maybeSingle();
          if (me?.full_name) setForm(f=>f.entry_by ? f : ({...f, entry_by: me.full_name}));
        }
      } catch (e) { /* ignore */ }
    })();
  },[]);

  const cats = dbCats.length > 0
    ? dbCats.filter(c => c.type === (form.type==='Cash In'?'in':'out') || c.type==='both').map(c=>c.name)
    : (form.type==='Cash In' ? CATEGORIES_IN : CATEGORIES_OUT);
  const payModes = dbPayModes.length > 0 ? dbPayModes.map(m=>m.name) : PAY_MODES;
  const parties = dbParties;

  async function save() {
    if (!form.amount || !form.category || !form.date) { toast.error('Date, Category and Amount are required'); return; }
    if (!form.payment_mode) { toast.error('Select a Payment Mode'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Amount must be greater than zero'); return; }
    setSaving(true);
    if (!(await assertUnlocked(form.company || company, [form.date, editing?.date]))) { setSaving(false); return; }
    const payload = { ...form, amount: Number(form.amount), source: editing?.source || 'manual', ...(projectId ? {project_id: projectId} : {}) };
    const { error } = editing
      ? await supabase.from('accounts_transactions').update(payload).eq('id', editing.id)
      : await supabase.from('accounts_transactions').insert(payload);
    if (error) toast.error(error.message);
    else {
      logAudit(user, editing ? 'update' : 'create', 'transaction', editing || payload, { summary: txnSummary(payload), before: txnSnap(editing), after: txnSnap(payload) });
      toast.success(editing?'Entry updated ✅':'Entry added ✅'); onSaved(); onClose();
    }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:600}}>
        <div className="modal-header">
          <span className="modal-title">{editing?'Edit':'Add'} Transaction</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label>Date *</label>
              <DateInput value={form.date} onChange={e=>sf('date',e.target.value)}/>
            </div>
            <div className="form-group"><label>Type *</label>
              <select value={form.type} onChange={e=>{sf('type',e.target.value);sf('category','');}}>
                {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Category *</label>
              <SearchableSelect
                options={cats.map(c=>({value:c,label:c}))}
                value={form.category||''}
                onChange={v=>sf('category',v)}
                placeholder="Select Category..."
                searchPlaceholder="Search category..."
              />
            </div>
            <div className="form-group"><label>Payment Type *</label>
              <select value={form.payment_type||'Cash'} onChange={e=>sf('payment_type',e.target.value)}>
                <option value="Cash">Cash</option>
                <option value="Online">Online</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            <div className="form-group"><label>Payment Mode *</label>
              <SearchableSelect
                options={payModes.map(m=>({value:m,label:m}))}
                value={form.payment_mode||''}
                onChange={v=>setForm(f=>({...f,payment_mode:v,payment_type:defaultPayType(v)}))}
                placeholder="Select Payment Mode..."
                searchPlaceholder="Search mode..."
              />
            </div>
            <div className="form-group"><label>Party Name</label>
              <SearchableSelect
                options={parties.map(p=>({value:p.party_name,label:`${p.party_name} (${p.party_type})`}))}
                value={form.party_name||''}
                onChange={v=>sf('party_name',v)}
                placeholder="Select Party..."
                searchPlaceholder="Search party..."
              />
            </div>
            <div className="form-group"><label>Amount (₹) *</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e=>sf('amount',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group"><label>Reference / Receipt No</label>
              <input value={form.reference} onChange={e=>sf('reference',e.target.value)} placeholder="Ref no, receipt no..."/>
            </div>
            <div className="form-group"><label>Entry By</label>
              <select value={form.entry_by||''} onChange={e=>sf('entry_by',e.target.value)}>
                <option value=''>Select...</option>
                {entryByStaff.map(s=><option key={s.full_name} value={s.full_name}>{s.full_name}{s.staff_category==='office_admin'?' (Office Admin)':s.staff_category==='director'?' (Director)':''}</option>)}
                {form.entry_by && !entryByStaff.some(s=>s.full_name===form.entry_by) && <option value={form.entry_by}>{form.entry_by}</option>}
              </select>
            </div>
            <div className="form-group full"><label>Description</label>
              <textarea rows={2} value={form.description} onChange={e=>sf('description',e.target.value)} placeholder="Details of the transaction..."/>
            </div>
          </div>
          {editing?.source && editing.source !== 'manual' && (
            <div style={{marginTop:10,padding:'8px 12px',background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:8,fontSize:12,color:'#1d4ed8'}}>
              ℹ️ This entry was auto-synced from <strong>{editing.source}</strong>. Editing here won't affect the source record.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Add Entry'}</button>
        </div>
      </div>

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 1. CASH BOOK
// ─────────────────────────────────────────────────────────────────
function CashBook({
  isEditor, company, accentColor='var(--accent)', projectId=null, refreshKey=0 }) {
  const { canEdit, user } = useAuth();
  const [showTransfer, setShowTransfer] = useState(false);
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [payMode,  setPayMode]  = useState('');
  const [filterCat,  setFilterCat]  = useState('');
  const [filterType, setFilterType] = useState('');
  const [datePreset, setDatePreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [dbCatsList, setDbCatsList] = useState([]);
  const [dbModesList, setDbModesList] = useState([]);
  const [partyName, setPartyName] = useState('');
  const [entryBy,   setEntryBy]   = useState('');
  const [partyList, setPartyList] = useState([]);
  const [entryByList, setEntryByList] = useState([]);

  useEffect(()=>{
    supabase.from('accounts_categories').select('name').order('name').then(({data})=>setDbCatsList((data||[]).map(x=>x.name)));
    supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setDbModesList((data||[]).map(x=>x.name)));
    supabase.from('accounts_parties').select('party_name').order('party_name').then(({data})=>setPartyList((data||[]).map(x=>x.party_name)));
    supabase.from('staff').select('full_name').in('staff_category',['office_admin','director']).eq('is_active',true).order('full_name').then(({data})=>setEntryByList((data||[]).map(x=>x.full_name)));
  },[]);
  const liveCats  = dbCatsList.length  > 0 ? dbCatsList  : ALL_CATS;
  const liveModes = dbModesList.length > 0 ? dbModesList : PAY_MODES;

  useEffect(()=>{
    supabase.from('accounts_parties').select('party_name').order('party_name').then(({data})=>setPartyList((data||[]).map(x=>x.party_name)));
    supabase.from('staff').select('full_name').in('staff_category',['office_admin','director']).eq('is_active',true).order('full_name').then(({data})=>setEntryByList((data||[]).map(x=>x.full_name)));
  },[]);

  const { from: fromDate, to: toDate } = getPresetRange(datePreset, customFrom, customTo);
  const { txns, setTxns, loading, reload } = useTxns({ company, payMode, fromDate, toDate, project_id: projectId, rk: refreshKey });

  // Opening (brought-forward) balance = net of everything dated before the period start, same company/account/project
  const [opening, setOpening] = useState(0);
  useEffect(() => {
    let dead = false;
    if (!fromDate) { setOpening(0); return; }
    (async () => {
      try {
        const prior = await fetchAllRows(() => {
          let q = supabase.from('accounts_transactions').select('type,amount').eq('company', company).lt('date', fromDate).order('id');
          if (payMode)   q = q.eq('payment_mode', payMode);
          if (projectId) q = q.eq('project_id', projectId);
          return q;
        });
        const v = prior.reduce((sum, r) => sum + (r.type === 'Cash In' ? Number(r.amount || 0) : r.type === 'Cash Out' ? -Number(r.amount || 0) : 0), 0);
        if (!dead) setOpening(v);
      } catch (e) { if (!dead) { setOpening(0); toast.error('Could not load opening balance: ' + e.message); } }
    })();
    return () => { dead = true; };
  }, [company, payMode, projectId, fromDate, refreshKey]);

  // Sort ascending for running balance
  const sorted = [...txns].sort((a,b)=>a.date.localeCompare(b.date)||a.created_at?.localeCompare(b.created_at||'')||0);

  // Running balance per day (always on full sorted list for accuracy)
  let balance = opening;
  const rows = sorted.map(t=>{
    const amt = Number(t.amount||0);
    if (t.type==='Cash In')  balance += amt;
    if (t.type==='Cash Out') balance -= amt;
    return { ...t, runBalance: balance };
  });

  // Apply category & type filters after balance calculation
  const filteredRows = rows.filter(r=>
    (!filterCat  || r.category === filterCat) &&
    (!filterType || r.type === filterType) &&
    (!partyName  || r.party_name === partyName) &&
    (!entryBy    || r.entry_by === entryBy)
  );

  const totalIn  = filteredRows.filter(t=>t.type==='Cash In').reduce((s,t)=>s+Number(t.amount||0),0);
  const totalOut = filteredRows.filter(t=>t.type==='Cash Out').reduce((s,t)=>s+Number(t.amount||0),0);

  async function del(row) {
    const synced = row.source && row.source !== 'manual';
    if (!window.confirm(synced
      ? `This entry was auto-posted from "${row.source}". Deleting it will NOT update the source record. Delete anyway?`
      : 'Delete this transaction?')) return;
    if (!(await assertUnlocked(row.company || company, [row.date]))) return;
    const { error } = await supabase.from('accounts_transactions').delete().eq('id', row.id);
    if (error) { toast.error('Delete failed: ' + error.message); return; }
    logAudit(user, 'delete', 'transaction', row, { summary: txnSummary(row), before: txnSnap(row) });
    toast.success('Deleted ✅'); setTxns(prev=>prev.filter(t=>t.id!==row.id)); reload();
  }

  async function bulkDelete() {
    const ids = [...selectedTxns].filter(id => filteredRows.some(r => r.id === id));   // only rows that are visible
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected entries? This cannot be undone.`)) return;
    const delRows = filteredRows.filter(r => ids.includes(r.id));
    if (!(await assertUnlocked(company, delRows.map(r => r.date)))) return;
    const { error } = await supabase.from('accounts_transactions').delete().in('id', ids);
    if (error) { toast.error('Delete failed: ' + error.message); return; }
    delRows.forEach(r => logAudit(user, 'delete', 'transaction', r, { summary: txnSummary(r) + ' (bulk delete)', before: txnSnap(r) }));
    setTxns(prev=>prev.filter(t=>!ids.includes(t.id)));
    setSelectedTxns(new Set());
    toast.success(`Deleted ${ids.length} entries`);
    reload();
  }

  function exportExcel() {
    const data = filteredRows.map((r,i)=>({
      '#':i+1, 'Date':r.date, 'Company':r.company, 'Type':r.type,
      'Category':r.category, 'Payment Type':r.payment_type||'', 'Payment Mode':r.payment_mode,
      'Party':r.party_name||'', 'Entry By':r.entry_by||'',
      'Cash In':r.type==='Cash In'?Number(r.amount):0,
      'Cash Out':r.type==='Cash Out'?Number(r.amount):0,
      'Balance':r.runBalance, 'Reference':r.reference||'',
      'Description':r.description||'', 'Source':r.source||'manual',
    }));
    if (fromDate) data.unshift({ '#':'', 'Date':fromDate, 'Company':company, 'Type':'', 'Category':'OPENING BALANCE', 'Payment Mode':payMode||'All accounts', 'Party':'', 'Entry By':'', 'Cash In':0, 'Cash Out':0, 'Balance':opening, 'Reference':'', 'Description':'Brought forward', 'Source':'' });
    saveStyledXlsx(`CashBook_${company}_${datePreset}${datePreset==='custom'?'_'+customFrom+'_to_'+customTo:''}.xlsx`, [{ name:'Cash Book', title:`Cash Book — ${company}`, rows:data, widths:{ '#':5 } }]);
  }

  const anyFilter = payMode||filterCat||filterType||partyName||entryBy;

  return (
    <div>
      {/* Summary cards */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          ...(fromDate ? [{ label:'Opening Balance', value:INR(opening), color: opening>=0?'var(--green)':'var(--red)' }] : []),
          { label:'Total Cash In',  value:INR(totalIn),           color:'var(--green)'  },
          { label:'Total Cash Out', value:INR(totalOut),          color:'var(--red)'    },
          { label:'Net Balance',    value:INR(totalIn-totalOut),  color: totalIn>=totalOut?'var(--green)':'var(--red)' },
          { label:'Closing Balance', value:INR(rows.length?rows[rows.length-1].runBalance:opening), color:(rows.length?rows[rows.length-1].runBalance:opening)>=0?'var(--green)':'var(--red)' },
          { label:'Transactions',   value:filteredRows.length,    color:'var(--accent)' },
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{color:s.color,fontSize:18}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters — all compact */}
      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <DatePresetFilter preset={datePreset} setPreset={setDatePreset} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} showAllTime={false}/>
          <select style={{width:80,fontSize:12,padding:'4px 6px'}} value={filterType} onChange={e=>setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="Cash In">↑ Cash In</option>
            <option value="Cash Out">↓ Cash Out</option>
          </select>
          <select style={{width:150,fontSize:12,padding:'4px 6px'}} value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {liveCats.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{width:145,fontSize:12,padding:'4px 6px'}} value={payMode} onChange={e=>setPayMode(e.target.value)}>
            <option value="">All Accounts</option>
            {liveModes.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select style={{width:140,fontSize:12,padding:'4px 6px'}} value={partyName} onChange={e=>setPartyName(e.target.value)}>
            <option value="">All Parties</option>
            {partyList.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select style={{width:140,fontSize:12,padding:'4px 6px'}} value={entryBy} onChange={e=>setEntryBy(e.target.value)}>
            <option value="">All Entry By</option>
            {entryByList.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          {anyFilter && <button className="btn btn-ghost btn-sm" onClick={()=>{setPayMode('');setFilterCat('');setFilterType('');setPartyName('');setEntryBy('');}}>✕ Clear</button>}
          <span style={{fontSize:11,color:'var(--text3)'}}>{filteredRows.length} entries</span>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button className="btn btn-secondary" onClick={exportExcel}><Download size={14}/> Export</button>
          {canEdit('accounts') && <button className="btn btn-secondary" onClick={()=>setShowTransfer(true)} title="Move money between two of your accounts (e.g. cash deposited to bank)">⇄ Transfer</button>}
          {canEdit('accounts') && <button className="btn btn-primary" onClick={()=>{setEditing(null);setShowModal(true);}}><Plus size={14}/> Add Entry</button>}
        </div>
      </div>

      {/* Cash Book Table */}
      {canEdit('accounts') && selectedTxns.size>0 && (
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
          <span style={{fontSize:12,color:'var(--accent)',fontWeight:600}}>{selectedTxns.size} selected</span>
          <button className="btn btn-danger btn-sm" onClick={bulkDelete}>🗑 Delete Selected</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setSelectedTxns(new Set())}>Clear selection</button>
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{width:36}}>{canEdit('accounts') && <input type="checkbox" checked={filteredRows.length>0&&filteredRows.every(r=>selectedTxns.has(r.id))} onChange={e=>setSelectedTxns(e.target.checked?new Set(filteredRows.map(t=>t.id)):new Set())}/>}</th>
              <th>Date</th><th>Type</th><th>Category</th>
              <th>Payment Type</th><th>Payment Mode</th><th style={{textAlign:'right'}}>Cash In (₹)</th>
              <th style={{textAlign:'right'}}>Cash Out (₹)</th>
              <th style={{textAlign:'right'}}>Balance (₹)</th>
              <th>Description</th><th>Source</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} style={{textAlign:'center',padding:20}}>Loading...</td></tr>}
            {!loading && fromDate && (
              <tr style={{background:'var(--bg3)'}}>
                <td></td>
                <td colSpan={7} style={{fontWeight:700,fontSize:12}}>Opening Balance (brought forward to {fromDate})</td>
                <td style={{textAlign:'right',fontWeight:700,fontSize:13,color:opening>=0?'var(--green)':'var(--red)'}}>{INR(opening)}</td>
                <td colSpan={3}></td>
              </tr>
            )}
            {!loading && filteredRows.length===0 && (
              <tr><td colSpan={12}><div className="empty-state"><BookOpen size={32}/><h3>No entries for this period</h3></div></td></tr>
            )}
            {filteredRows.map((r,i)=>(
              <tr key={r.id} style={{background: r.type==='Cash In'?'rgba(22,163,74,0.04)':'rgba(220,38,38,0.04)'}}>
                <td>{canEdit('accounts') && <input type="checkbox" checked={selectedTxns.has(r.id)} onChange={()=>setSelectedTxns(p=>{const n=new Set(p);n.has(r.id)?n.delete(r.id):n.add(r.id);return n;})}/>}</td>
                <td style={{fontWeight:600,fontSize:12,whiteSpace:'nowrap'}}>{fmtDMY(r.date)}</td>
                <td>
                  <span style={{fontWeight:700,fontSize:12,color:r.type==='Cash In'?'var(--green)':'var(--red)'}}>
                    {r.type==='Cash In'?'↑ IN':'↓ OUT'}
                  </span>
                </td>
                <td style={{fontSize:12}}>{r.category}</td>
                <td style={{fontSize:11}}>{r.payment_type||'—'}</td>
                <td style={{fontSize:11}}>{r.payment_mode}</td>
                <td style={{textAlign:'right',fontWeight:600,color:'var(--green)',fontSize:13}}>
                  {r.type==='Cash In'?INR(r.amount):''}
                </td>
                <td style={{textAlign:'right',fontWeight:600,color:'var(--red)',fontSize:13}}>
                  {r.type==='Cash Out'?INR(r.amount):''}
                </td>
                <td style={{textAlign:'right',fontWeight:700,fontSize:13,color:r.runBalance>=0?'var(--green)':'var(--red)'}}>
                  {INR(r.runBalance)}
                </td>
                <td style={{fontSize:11,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.description}>{r.description||'—'}</td>
                <td>
                  <span style={{fontSize:9,padding:'2px 6px',borderRadius:4,background: r.source==='manual'?'var(--bg3)':'#dbeafe',color:r.source==='manual'?'var(--text3)':'#1d4ed8'}}>
                    {r.source==='manual'?'Manual':r.source}
                  </span>
                </td>
                <td>
                  <div style={{display:'flex',gap:4}}>
                    {canEdit('accounts') && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(r);setShowModal(true);}}><Edit2 size={13}/></button>}
                    {canEdit('accounts') && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} title="Delete" onClick={()=>del(r)}><Trash2 size={13}/></button>}
                  </div>
                </td>
              </tr>
            ))}
            {/* Totals row */}
            {filteredRows.length>0 && (
              <tr style={{background:'var(--bg3)',fontWeight:700}}>
                <td colSpan={6} style={{textAlign:'right',padding:'8px 12px'}}>
                  {anyFilter ? `FILTERED TOTAL (${filteredRows.length} entries)` : 'TOTAL'}
                </td>
                <td style={{textAlign:'right',color:'var(--green)',fontSize:14}}>
                  {INR(filteredRows.filter(r=>r.type==='Cash In').reduce((s,r)=>s+Number(r.amount||0),0))}
                </td>
                <td style={{textAlign:'right',color:'var(--red)',fontSize:14}}>
                  {INR(filteredRows.filter(r=>r.type==='Cash Out').reduce((s,r)=>s+Number(r.amount||0),0))}
                </td>
                <td style={{textAlign:'right',color:totalIn>=totalOut?'var(--green)':'var(--red)',fontSize:14}}>{INR(totalIn-totalOut)}</td>
                <td colSpan={3}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && <EntryModal onClose={()=>setShowModal(false)} onSaved={reload} editing={editing} company={company} projectId={projectId}/>}
      {showTransfer && <TransferModal onClose={()=>setShowTransfer(false)} onSaved={reload} company={company} projectId={projectId} modes={liveModes} user={user}/>}

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// Transfer between two of the company's own accounts: posts a matched Cash Out + Cash In (category "Internal Transfer"),
// which keeps each account's balance right and is left out of Income/Expense in the Financial Report.
function TransferModal({ onClose, onSaved, company, projectId, modes, user }) {
  const [form, setForm] = useState({ date: todayStr(), from: '', to: '', amount: '', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));
  async function save() {
    if (!form.from || !form.to || form.from === form.to) { toast.error('Choose two different accounts'); return; }
    if (!(Number(form.amount) > 0)) { toast.error('Amount must be greater than zero'); return; }
    setSaving(true);
    try {
      if (!(await assertUnlocked(company, [form.date]))) { setSaving(false); return; }
      const { data: cat } = await supabase.from('accounts_categories').select('id').eq('name', TRANSFER_CATEGORY).limit(1);
      if (!cat || cat.length === 0) await supabase.from('accounts_categories').insert({ name: TRANSFER_CATEGORY, type: 'both', category_type: 'Finance', company: 'ALL' });
      const base = { company, date: form.date, category: TRANSFER_CATEGORY, amount: Number(form.amount), reference: form.reference || null, source: 'transfer', entry_by: user?.email || null, ...(projectId ? { project_id: projectId } : {}) };
      const rows = [
        { ...base, type: 'Cash Out', payment_mode: form.from, description: `Transfer to ${form.to}${form.notes ? ' - ' + form.notes : ''}` },
        { ...base, type: 'Cash In',  payment_mode: form.to,   description: `Transfer from ${form.from}${form.notes ? ' - ' + form.notes : ''}` },
      ];
      const { error } = await supabase.from('accounts_transactions').insert(rows);
      if (error) throw error;
      logAudit(user, 'create', 'transfer', { company }, { summary: `Transfer ₹${Number(form.amount).toLocaleString('en-IN')} ${form.from} → ${form.to} on ${form.date}`, after: { ...form } });
      toast.success('Transfer recorded ✅'); onSaved(); onClose();
    } catch (e) { toast.error('Transfer failed: ' + e.message); }
    setSaving(false);
  }
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:460}}>
        <div className="modal-header"><span className="modal-title">⇄ Transfer Between Accounts</span><button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button></div>
        <div className="modal-body"><div className="form-grid">
          <div className="form-group"><label>Date *</label><DateInput value={form.date} onChange={e=>sf('date',e.target.value)}/></div>
          <div className="form-group"><label>Amount (₹) *</label><input type="number" min="0" step="0.01" value={form.amount} onChange={e=>sf('amount',e.target.value)}/></div>
          <div className="form-group"><label>From account *</label>
            <select value={form.from} onChange={e=>sf('from',e.target.value)}><option value="">Select...</option>{modes.map(m=><option key={m} value={m}>{m}</option>)}</select></div>
          <div className="form-group"><label>To account *</label>
            <select value={form.to} onChange={e=>sf('to',e.target.value)}><option value="">Select...</option>{modes.map(m=><option key={m} value={m}>{m}</option>)}</select></div>
          <div className="form-group"><label>Reference</label><input value={form.reference} onChange={e=>sf('reference',e.target.value)} placeholder="Deposit slip / UTR"/></div>
          <div className="form-group"><label>Notes</label><input value={form.notes} onChange={e=>sf('notes',e.target.value)}/></div>
        </div></div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Record Transfer'}</button></div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. MONTHLY SUMMARY
// ─────────────────────────────────────────────────────────────────
function MonthlySummary({ company, accentColor='var(--accent)', projectId=null }) {
  const [year,    setYear]    = useState(curYear);
  const [viewBy,  setViewBy]  = useState('category'); // category | paymode
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const toggleExpand = (mo) => setExpandedMonths(prev => { const n = new Set(prev); n.has(mo) ? n.delete(mo) : n.add(mo); return n; });
  const [rpcRows, setRpcRows] = useState([]);
  const [rpcLoading, setRpcLoading] = useState(false);

  useEffect(()=>{
    setRpcLoading(true);
    supabase.rpc('accounts_monthly_summary', { p_company: company, p_year: String(year), p_project_id: projectId })
      .then(({data, error})=>{ if(error) console.error('monthly summary rpc error:',error); setRpcRows(data||[]); setRpcLoading(false); });
  }, [company, year, projectId]);

  // Group the small pre-aggregated RPC result by month (DB already did the SUM/GROUP BY — this is just reshaping)
  const monthData = MONTHS.map((mLabel,mi)=>{
    const mo = mi+1;
    const mRows = rpcRows.filter(r=>r.month===mo);
    const totalIn  = mRows.filter(r=>r.type==='Cash In').reduce((s,r)=>s+Number(r.total||0),0);
    const totalOut = mRows.filter(r=>r.type==='Cash Out').reduce((s,r)=>s+Number(r.total||0),0);
    const groups = {};
    mRows.forEach(r=>{
      const key = viewBy==='category' ? r.category : r.payment_mode;
      if (!groups[key]) groups[key] = { in:0, out:0 };
      if (r.type==='Cash In')  groups[key].in  += Number(r.total||0);
      if (r.type==='Cash Out') groups[key].out += Number(r.total||0);
    });
    return { mLabel, mo, totalIn, totalOut, net: totalIn-totalOut, groups };
  });

  const yearIn  = monthData.reduce((s,m)=>s+m.totalIn,0);
  const yearOut = monthData.reduce((s,m)=>s+m.totalOut,0);

  function exportExcel() {
    const data = monthData.map(m=>({
      'Month':m.mLabel, 'Total Cash In':m.totalIn, 'Total Cash Out':m.totalOut, 'Net':m.net
    }));
    void rpcLoading; // (kept for lint/no-unused; loading state shown in UI below)
    data.push({ 'Month':'TOTAL', 'Total Cash In':yearIn, 'Total Cash Out':yearOut, 'Net':yearIn-yearOut });
    saveStyledXlsx(`MonthlySummary_${company}_${year}.xlsx`, [{ name:'Monthly Summary', title:`Monthly Summary ${year} — ${company}`, rows:data }]);
  }

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <select style={{width:100,fontSize:12}} value={year} onChange={e=>setYear(e.target.value==='all'?'all':Number(e.target.value))}>
            <option value="all">All Years</option>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select style={{width:130,fontSize:12}} value={viewBy} onChange={e=>setViewBy(e.target.value)}>
            <option value="category">By Category</option>
            <option value="paymode">By Payment Mode</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={exportExcel}><Download size={14}/> Export Excel</button>
      </div>

      {/* Year totals */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          {label:`${year==='all'?'All-time':year} Total Cash In`,  value:INR(yearIn),        color:'var(--green)'},
          {label:`${year==='all'?'All-time':year} Total Cash Out`, value:INR(yearOut),       color:'var(--red)'},
          {label:`${year==='all'?'All-time':year} Net`,            value:INR(yearIn-yearOut),color:yearIn>=yearOut?'var(--green)':'var(--red)'},
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{color:s.color,fontSize:18}}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{fontSize:11,color:'var(--text3)',marginBottom:8}}>Totals include internal transfers between accounts (they add equally to Cash In and Cash Out). The Financial Report tab leaves them out.</div>
      {/* Monthly table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Month</th><th style={{textAlign:'right'}}>Cash In (₹)</th>
              <th style={{textAlign:'right'}}>Cash Out (₹)</th>
              <th style={{textAlign:'right'}}>Net (₹)</th>
              <th>Breakdown</th>
            </tr>
          </thead>
          <tbody>
            {monthData.map(m=>(
              <tr key={m.mo} style={{background:m.mo===curMonth&&year===curYear?'var(--accent-light)':undefined}}>
                <td style={{fontWeight:700}}>{m.mLabel} {year}</td>
                <td style={{textAlign:'right',color:'var(--green)',fontWeight:600}}>{m.totalIn>0?INR(m.totalIn):'—'}</td>
                <td style={{textAlign:'right',color:'var(--red)',fontWeight:600}}>{m.totalOut>0?INR(m.totalOut):'—'}</td>
                <td style={{textAlign:'right',fontWeight:700,color:m.net>=0?'var(--green)':'var(--red)'}}>{INR(m.net)}</td>
                <td style={{fontSize:11}}>
                  {(() => {
                    const sortedEntries = Object.entries(m.groups).sort((a,b) => (b[1].in+b[1].out) - (a[1].in+a[1].out));
                    const isExpanded = expandedMonths.has(m.mo);
                    const visibleEntries = isExpanded ? sortedEntries : sortedEntries.slice(0,5);
                    const hiddenCount = sortedEntries.length - 5;
                    return (
                      <>
                        {visibleEntries.map(([k,v])=>(
                          <span key={k} style={{marginRight:10,whiteSpace:'nowrap',display:'inline-block',marginBottom:3}}>
                            <span style={{color:'var(--text3)'}}>{k}:</span>{' '}
                            {v.in>0&&<span style={{color:'var(--green)'}}>+{INR(v.in)}</span>}
                            {v.in>0&&v.out>0&&' '}
                            {v.out>0&&<span style={{color:'var(--red)'}}>-{INR(v.out)}</span>}
                          </span>
                        ))}
                        {hiddenCount>0&&(
                          <button onClick={()=>toggleExpand(m.mo)} style={{fontSize:11,color:'var(--accent)',background:'none',border:'none',cursor:'pointer',padding:0,fontWeight:600,textDecoration:'underline'}}>
                            {isExpanded ? '▲ Show less' : `+${hiddenCount} more`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:'var(--bg3)',fontWeight:700}}>
              <td>TOTAL {year}</td>
              <td style={{textAlign:'right',color:'var(--green)'}}>{INR(yearIn)}</td>
              <td style={{textAlign:'right',color:'var(--red)'}}>{INR(yearOut)}</td>
              <td style={{textAlign:'right',color:yearIn>=yearOut?'var(--green)':'var(--red)'}}>{INR(yearIn-yearOut)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. ACCOUNT BALANCES
// ─────────────────────────────────────────────────────────────────
function AccountBalances({ company, accentColor='var(--accent)', projectId=null }) {
  const [year,    setYear]    = useState(curYear);
  const [modeRows, setModeRows] = useState([]);
  const [catRows,  setCatRows]  = useState([]);
  const [rpcLoading, setRpcLoading] = useState(false);
  const [openByMode, setOpenByMode] = useState({});   // balance brought forward from earlier years, per payment mode

  useEffect(()=>{
    setRpcLoading(true);
    const priorYears = year === 'all' ? [] : YEARS.filter(y => y < Number(year));
    Promise.all([
      supabase.rpc('accounts_balance_by_mode',     { p_company: company, p_year: String(year), p_project_id: projectId }),
      supabase.rpc('accounts_balance_by_category', { p_company: company, p_year: String(year), p_project_id: projectId }),
      ...priorYears.map(y => supabase.rpc('accounts_balance_by_mode', { p_company: company, p_year: String(y), p_project_id: projectId })),
    ]).then(([m,c,...prior])=>{
      if (m.error) { console.error('balance by mode rpc error:', m.error); toast.error('Could not load account balances'); }
      if (c.error) console.error('balance by category rpc error:', c.error);
      const open = {};
      prior.forEach(p => {
        if (p.error) { console.error('prior-year balance rpc error:', p.error); return; }
        (p.data||[]).forEach(r => {
          if (!r.payment_mode) return;
          open[r.payment_mode] = (open[r.payment_mode]||0) + (r.type==='Cash In' ? 1 : r.type==='Cash Out' ? -1 : 0) * Number(r.total||0);
        });
      });
      setOpenByMode(open);
      setModeRows(m.data||[]); setCatRows(c.data||[]); setRpcLoading(false);
    });
  }, [company, year, projectId]);

  // Balance per payment mode — derived from actual RPC result (covers ALL real payment modes, not just hardcoded ones)
  const allModesSeen = [...new Set([...modeRows.map(r=>r.payment_mode), ...Object.keys(openByMode)].filter(Boolean))].sort();
  const modeBalances = allModesSeen.map(mode=>{
    const rows = modeRows.filter(r=>r.payment_mode===mode);
    const totalIn  = rows.filter(r=>r.type==='Cash In').reduce((s,r)=>s+Number(r.total||0),0);
    const totalOut = rows.filter(r=>r.type==='Cash Out').reduce((s,r)=>s+Number(r.total||0),0);
    const opening  = openByMode[mode] || 0;
    const balance  = opening + totalIn - totalOut;   // closing balance at end of the selected year
    const count    = rows.reduce((s,r)=>s+Number(r.txn_count||0),0);
    return { mode, opening, totalIn, totalOut, balance, count };
  }).filter(m=>m.count>0 || Math.abs(m.opening)>0.004);

  // Category breakdown — derived from actual RPC result (covers ALL real categories, not just hardcoded ones)
  const allCatsSeen = [...new Set(catRows.map(r=>r.category).filter(Boolean))].sort();
  const catBreakdown = allCatsSeen.map(cat=>{
    const rows = catRows.filter(r=>r.category===cat);
    const totalIn  = rows.filter(r=>r.type==='Cash In').reduce((s,r)=>s+Number(r.total||0),0);
    const totalOut = rows.filter(r=>r.type==='Cash Out').reduce((s,r)=>s+Number(r.total||0),0);
    const count    = rows.reduce((s,r)=>s+Number(r.txn_count||0),0);
    return { cat, totalIn, totalOut, count };
  }).filter(c=>c.count>0);

  function exportExcel() {
    saveStyledXlsx(`AccountBalances_${company}_${year}.xlsx`, [
      { name:'Balances', title:`Account Balances ${year} — ${company}`, rows:modeBalances.map(m=>({'Account':m.mode,'Opening':m.opening,'Cash In':m.totalIn,'Cash Out':m.totalOut,'Closing Balance':m.balance,'Transactions':m.count})) },
      { name:'Category', title:`Category Breakdown ${year} — ${company}`, rows:catBreakdown.map(c=>({'Category':c.cat,'Cash In':c.totalIn,'Cash Out':c.totalOut,'Count':c.count})) },
    ]);
  }

  const totalBalance = modeBalances.reduce((s,m)=>s+m.balance,0);

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <select style={{width:100,fontSize:12}} value={year} onChange={e=>setYear(e.target.value==='all'?'all':Number(e.target.value))}>
            <option value="all">All Years</option>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary" onClick={exportExcel}><Download size={14}/> Export Excel</button>
      </div>

      {/* Total across all accounts */}
      <div className="stat-card" style={{marginBottom:16,padding:'16px 20px',border:'2px solid var(--accent)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div className="stat-label">Closing Balance — All Accounts ({company}{year==='all'?'':`, end of ${year}`})</div>
            <div style={{fontSize:28,fontWeight:900,color:totalBalance>=0?'var(--green)':'var(--red)',marginTop:4}}>{INR(totalBalance)}</div>
          </div>
          <div style={{fontSize:32,opacity:0.3}}>🏦</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr',gap:16}}>
        {/* Per Account */}
        <div>
          <div style={{fontWeight:700,fontSize:14,marginBottom:10,color:'var(--text2)',paddingTop:8}}>Balance by Payment Account</div>
          {modeBalances.length===0
            ? <div style={{textAlign:'center',color:'var(--text3)',padding:20}}>No transactions</div>
            : <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {modeBalances.map(m=>(
                  <div key={m.mode} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px'}}>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:8,color:'var(--text)'}}>{m.mode}</div>
                    <div style={{display:'grid',gridTemplateColumns:year==='all'?'1fr 1fr 1fr':'1fr 1fr 1fr 1fr',gap:8}}>
                      {year!=='all'&&<div style={{textAlign:'center'}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Opening</div>
                        <div style={{fontWeight:600,fontSize:12,color:m.opening>=0?'var(--text)':'var(--red)'}}>{INR(m.opening)}</div>
                      </div>}
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Cash In</div>
                        <div style={{fontWeight:600,fontSize:12,color:'var(--green)'}}>{INR(m.totalIn)}</div>
                      </div>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Cash Out</div>
                        <div style={{fontWeight:600,fontSize:12,color:'var(--red)'}}>{INR(m.totalOut)}</div>
                      </div>
                      <div style={{textAlign:'center'}}>
                        <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>Closing</div>
                        <div style={{fontWeight:800,fontSize:13,color:m.balance>=0?'var(--green)':'var(--red)'}}>{INR(m.balance)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>

        {/* Per Category */}
        <div>
          <div style={{fontWeight:700,fontSize:14,marginBottom:10,color:'var(--text2)',paddingTop:8}}>Breakdown by Category</div>
          {catBreakdown.length===0
            ? <div style={{textAlign:'center',color:'var(--text3)',padding:20}}>No transactions</div>
            : <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {catBreakdown.map(c=>(
                  <div key={c.cat} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                    <div style={{fontWeight:500,fontSize:13,color:'var(--text)',minWidth:120}}>{c.cat}</div>
                    <div style={{display:'flex',gap:16}}>
                      {c.totalIn>0&&<div style={{textAlign:'right'}}>
                        <div style={{fontSize:10,color:'var(--text3)'}}>In</div>
                        <div style={{fontWeight:600,fontSize:12,color:'var(--green)'}}>{INR(c.totalIn)}</div>
                      </div>}
                      {c.totalOut>0&&<div style={{textAlign:'right'}}>
                        <div style={{fontSize:10,color:'var(--text3)'}}>Out</div>
                        <div style={{fontWeight:600,fontSize:12,color:'var(--red)'}}>{INR(c.totalOut)}</div>
                      </div>}
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. ALL ENTRIES (with bulk import)
// ─────────────────────────────────────────────────────────────────
function AllEntries({
  isEditor, company, accentColor='var(--accent)', projectId=null, refreshKey=0 }) {
  const { canEdit, user } = useAuth();
  const PAGE_SIZE = 50;
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [type,     setType]     = useState('');
  const [category, setCategory] = useState('');
  const [payMode,  setPayMode]  = useState('');
  const [search,   setSearch]   = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const { from: fromDate, to: toDate } = getPresetRange(datePreset, customFrom, customTo);
  const [showModal,  setShowModal]  = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [page,     setPage]     = useState(0);
  const [pageTxns, setPageTxns] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [totalCount,  setTotalCount]  = useState(0);
  const [dbCatsList, setDbCatsList] = useState([]);
  const [dbModesList, setDbModesList] = useState([]);
  const [partyName, setPartyName] = useState('');
  const [entryBy,   setEntryBy]   = useState('');
  const [partyList, setPartyList] = useState([]);
  const [entryByList, setEntryByList] = useState([]);

  useEffect(()=>{
    supabase.from('accounts_categories').select('name').order('name').then(({data})=>setDbCatsList((data||[]).map(x=>x.name)));
    supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setDbModesList((data||[]).map(x=>x.name)));
    supabase.from('accounts_parties').select('party_name').order('party_name').then(({data})=>setPartyList((data||[]).map(x=>x.party_name)));
    supabase.from('staff').select('full_name').in('staff_category',['office_admin','director']).eq('is_active',true).order('full_name').then(({data})=>setEntryByList((data||[]).map(x=>x.full_name)));
  },[]);
  const liveCats  = dbCatsList.length  > 0 ? dbCatsList  : ALL_CATS;
  const liveModes = dbModesList.length > 0 ? dbModesList : PAY_MODES;

  // Debounce the search box and strip characters that would break the PostgREST .or() filter
  const [debSearch, setDebSearch] = useState('');
  useEffect(()=>{ const t = setTimeout(()=>setDebSearch(search.replace(/[,()%*\\]/g,' ').trim()), 350); return ()=>clearTimeout(t); }, [search]);

  // Reset to page 0 whenever a filter changes
  useEffect(()=>{ setPage(0); }, [company, type, category, payMode, partyName, entryBy, datePreset, customFrom, customTo, debSearch, projectId]);

  const loadPage = useCallback(async () => {
    setPageLoading(true);
    let q = supabase.from('accounts_transactions')
      .select('*', { count: 'exact' })
      .order('date',{ascending:false})
      .order('created_at',{ascending:false})
      .order('id',{ascending:false})
      .range(page*PAGE_SIZE, page*PAGE_SIZE+PAGE_SIZE-1);
    if (company)    q = q.eq('company', company);
    if (projectId)  q = q.eq('project_id', projectId);
    if (type)       q = q.eq('type', type);
    if (category)   q = q.eq('category', category);
    if (payMode)     q = q.eq('payment_mode', payMode);
    if (partyName)   q = q.eq('party_name', partyName);
    if (entryBy)     q = q.eq('entry_by', entryBy);
    if (fromDate)    q = q.gte('date', fromDate);
    if (toDate)      q = q.lte('date', toDate);
    if (debSearch)   q = q.or(`description.ilike.%${debSearch}%,reference.ilike.%${debSearch}%,category.ilike.%${debSearch}%,party_name.ilike.%${debSearch}%`);
    const { data, count, error } = await q;
    if (error) toast.error('Could not load entries: ' + error.message);
    setPageTxns(data||[]);
    setTotalCount(count||0);
    setPageLoading(false);
  }, [company, projectId, type, category, payMode, partyName, entryBy, fromDate, toDate, debSearch, page, refreshKey]); // fromDate/toDate derived from datePreset/customFrom/customTo each render

  useEffect(()=>{ loadPage(); }, [loadPage]);

  const filtered = pageTxns; // already filtered server-side
  const totalPages = Math.max(1, Math.ceil(totalCount/PAGE_SIZE));
  const reload = loadPage;
  const loading = pageLoading;
  const txns = pageTxns;

  async function del(row) {
    const synced = row.source && row.source !== 'manual';
    if (!window.confirm(synced
      ? `This entry was auto-posted from "${row.source}". Deleting it will NOT update the source record. Delete anyway?`
      : 'Delete this transaction?')) return;
    if (!(await assertUnlocked(row.company || company, [row.date]))) return;
    const { error } = await supabase.from('accounts_transactions').delete().eq('id', row.id);
    if (error) { toast.error('Delete failed: ' + error.message); return; }
    logAudit(user, 'delete', 'transaction', row, { summary: txnSummary(row), before: txnSnap(row) });
    toast.success('Deleted ✅'); loadPage();
  }

  async function exportExcel() {
    toast.success('Preparing export...');
    let all = [];
    try {
      all = await fetchAllRows(() => {
        let q = supabase.from('accounts_transactions').select('*').order('date',{ascending:false}).order('created_at',{ascending:false}).order('id',{ascending:false});
        if (company)   q = q.eq('company', company);
        if (projectId) q = q.eq('project_id', projectId);
        if (type)      q = q.eq('type', type);
        if (category)  q = q.eq('category', category);
        if (payMode)   q = q.eq('payment_mode', payMode);
        if (partyName) q = q.eq('party_name', partyName);
        if (entryBy)   q = q.eq('entry_by', entryBy);
        if (fromDate)  q = q.gte('date', fromDate);
        if (toDate)    q = q.lte('date', toDate);
        if (debSearch) q = q.or(`description.ilike.%${debSearch}%,reference.ilike.%${debSearch}%,category.ilike.%${debSearch}%,party_name.ilike.%${debSearch}%`);
        return q;
      });
    } catch (e) { toast.error('Export failed: ' + e.message); return; }
    const data = all.map((r,i)=>({
      '#':i+1,'Date':r.date,'Company':r.company,'Type':r.type,'Category':r.category,
      'Payment Type':r.payment_type||'','Payment Mode':r.payment_mode,'Party':r.party_name||'','Amount':Number(r.amount),'Reference':r.reference||'',
      'Description':r.description||'','Entry By':r.entry_by||'','Source':r.source||'manual',
    }));
    saveStyledXlsx('Accounts_Export.xlsx', [{ name:'Transactions', title:'Accounts Transactions', rows:data, widths:{ '#':5 } }]);
    toast.success(`Exported ${all.length} entries`);
  }

  function downloadSample() {
    const sample = [
      {Company:'ARMSTECH',Date:'01-05-2026',Type:'Cash In',Category:'Tuition Fees','Payment Mode':'EKM FDO',Party:'',Amount:10000,Reference:'RCP001',Description:'Student fee - Reg EM47001','Entry By':'Anjana S Nair'},
      {Company:'ARMSTECH',Date:'02-05-2026',Type:'Cash Out',Category:'Salary','Payment Mode':'SBI',Party:'',Amount:25000,Reference:'',Description:'Staff salary - May 2026','Entry By':'Director Name'},
      {Company:'SLCCS',Date:'03-05-2026',Type:'Cash In',Category:'Other Income','Payment Mode':'Cash',Party:'',Amount:5000,Reference:'',Description:'Miscellaneous income','Entry By':''},
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sample), 'Import Format');
    XLSX.writeFile(wb, 'Accounts_Import_Sample.xlsx');
  }

  return (
    <div>
      {/* Filters */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:200}}>
            <Search/><input placeholder="Search description, ref, category..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={{width:100,fontSize:12}} value={type} onChange={e=>setType(e.target.value)}>
            <option value="">All Types</option>
            {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <select style={{width:150,fontSize:12}} value={category} onChange={e=>setCategory(e.target.value)}>
            <option value="">All Categories</option>
            {liveCats.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{width:140,fontSize:12}} value={payMode} onChange={e=>setPayMode(e.target.value)}>
            <option value="">All Accounts</option>
            {liveModes.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
          <select style={{width:140,fontSize:12}} value={partyName} onChange={e=>setPartyName(e.target.value)}>
            <option value="">All Parties</option>
            {partyList.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select style={{width:140,fontSize:12}} value={entryBy} onChange={e=>setEntryBy(e.target.value)}>
            <option value="">All Entry By</option>
            {entryByList.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <DatePresetFilter preset={datePreset} setPreset={setDatePreset} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo}/>
          {(type||category||payMode||partyName||entryBy||datePreset!=='all'||search)&&
            <button className="btn btn-ghost btn-sm" onClick={()=>{setType('');setCategory('');setPayMode('');setPartyName('');setEntryBy('');setDatePreset('all');setCustomFrom('');setCustomTo('');setSearch('');}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{totalCount} entries (page {page+1} of {totalPages})</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={exportExcel}><Download size={14}/> Export</button>
          {canEdit('accounts')&&<button className="btn btn-secondary" onClick={()=>setShowImport(true)}><Upload size={14}/> Import Excel</button>}
          {canEdit('accounts')&&<button className="btn btn-primary" onClick={()=>{setEditing(null);setShowModal(true);}}><Plus size={14}/> Add Entry</button>}
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th style={{width:36}}><input type="checkbox" checked={selectedTxns.size===txns.length&&txns.length>0} onChange={e=>setSelectedTxns(e.target.checked?new Set(txns.map(t=>t.id)):new Set())}/></th><th>Date</th><th>Type</th><th>Category</th><th>Payment Type</th><th>Account</th><th style={{textAlign:'right'}}>Amount (₹)</th><th>Description</th><th>Source</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading&&<tr><td colSpan={10} style={{textAlign:'center',padding:20}}>Loading...</td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={10}><div className="empty-state"><DollarSign size={32}/><h3>No transactions found</h3></div></td></tr>}
            {filtered.map(r=>(
              <tr key={r.id}>
                <td><input type="checkbox" checked={selectedTxns.has(r.id)} onChange={()=>setSelectedTxns(p=>{const n=new Set(p);n.has(r.id)?n.delete(r.id):n.add(r.id);return n;})}/></td>
                <td style={{fontWeight:600,fontSize:12}}>{fmtDMY(r.date)}</td>
                <td><span style={{fontWeight:700,fontSize:11,color:r.type==='Cash In'?'var(--green)':'var(--red)'}}>{r.type==='Cash In'?'↑ IN':'↓ OUT'}</span></td>
                <td style={{fontSize:12}}>{r.category}</td>
                <td style={{fontSize:11}}>{r.payment_type||'—'}</td>
                <td style={{fontSize:11}}>{r.payment_mode}</td>
                <td style={{textAlign:'right',fontWeight:700,fontSize:13,color:r.type==='Cash In'?'var(--green)':'var(--red)'}}>{INR(r.amount)}</td>
                <td style={{fontSize:11,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.description}>{r.description||'—'}</td>
                <td><span style={{fontSize:9,padding:'2px 6px',borderRadius:4,background:r.source==='manual'?'var(--bg3)':'#dbeafe',color:r.source==='manual'?'var(--text3)':'#1d4ed8'}}>{r.source==='manual'?'Manual':r.source}</span></td>
                <td>
                  <div style={{display:'flex',gap:4}}>
                    {canEdit('accounts')&&<button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(r);setShowModal(true);}}><Edit2 size={13}/></button>}
                    {canEdit('accounts')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} title="Delete" onClick={()=>del(r)}><Trash2 size={13}/></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages>1&&(
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:10,marginTop:14}}>
          <button className="btn btn-secondary btn-sm" disabled={page===0} onClick={()=>setPage(p=>Math.max(0,p-1))}>← Previous</button>
          <span style={{fontSize:12,color:'var(--text3)'}}>Page {page+1} of {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page>=totalPages-1} onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))}>Next →</button>
        </div>
      )}

      {showModal && <EntryModal onClose={()=>setShowModal(false)} onSaved={reload} editing={editing} company={company} projectId={projectId}/>}
      {showImport && <ImportModal onClose={()=>setShowImport(false)} onSaved={reload} onSample={downloadSample}/>}

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// IMPORT MODAL
// ─────────────────────────────────────────────────────────────────
// Accepts DD-MM-YYYY, DD/MM/YYYY, DD.MM.YY, YYYY-MM-DD and Excel date serials. Returns 'YYYY-MM-DD' or null when it is not a real date.
function parseImportDate(raw) {
  const v = String(raw ?? '').trim();
  if (!v) return null;
  let y, m, d;
  let mt;
  if ((mt = v.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/))) { y = +mt[1]; m = +mt[2]; d = +mt[3]; }
  else if ((mt = v.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/))) { d = +mt[1]; m = +mt[2]; y = +mt[3]; if (y < 100) y += 2000; }
  else if (/^\d{4,6}(\.\d+)?$/.test(v) && +v > 20000 && +v < 80000) {
    const jd = new Date(Date.UTC(1899, 11, 30) + Math.floor(parseFloat(v)) * 86400000);
    y = jd.getUTCFullYear(); m = jd.getUTCMonth() + 1; d = jd.getUTCDate();
  } else return null;
  const chk = new Date(Date.UTC(y, m - 1, d));
  if (y < 2000 || y > 2100 || chk.getUTCFullYear() !== y || chk.getUTCMonth() !== m - 1 || chk.getUTCDate() !== d) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}
const IMPORT_IN  = ['cashin','in','income','credit','receipt','receipts','cr'];
const IMPORT_OUT = ['cashout','out','expense','expenses','debit','payment','payments','dr'];

function ImportModal({ onClose, onSaved, onSample }) {
  const [rows,     setRows]     = useState([]);
  const [errors,   setErrors]   = useState([]);
  const [importing,setImporting]= useState(false);
  const [step,     setStep]     = useState('upload'); // upload | preview | done
  const [result,   setResult]   = useState(null);
  const [newCats, setNewCats] = useState([]);
  const [newModes, setNewModes] = useState([]);
  const [unknownEntryBy, setUnknownEntryBy] = useState([]);
  const [includeDups, setIncludeDups] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = React.useRef();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, {type:'array'});
      const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {header:1,defval:''});
      if (raw.length<2) { toast.error('File empty'); setParsing(false); return; }
      // Fresh master data every time (never parse against a half-loaded list)
      const [cats, modes, staffNames] = await Promise.all([
        supabase.from('accounts_categories').select('name'),
        supabase.from('accounts_payment_modes').select('name'),
        supabase.from('staff').select('full_name').in('staff_category',['office_admin','director']),
      ]);
      if (cats.error || modes.error) throw (cats.error || modes.error);
      const catMap  = new Map((cats.data||[]).map(x=>[x.name.trim().toLowerCase(), x.name]));
      const modeMap = new Map((modes.data||[]).map(x=>[x.name.trim().toLowerCase(), x.name]));
      const staffMap = new Map((staffNames.data||[]).map(x=>[x.full_name.trim().toLowerCase(), x.full_name]));

      const headers = raw[0].map(h=>String(h).trim().toLowerCase());
      const col = (h) => { let i = headers.indexOf(h); if (i < 0) i = headers.findIndex(x=>x.includes(h)); return i; };
      const dataRows = raw.slice(1).map((r,i)=>({r, n:i+2})).filter(({r})=>r.some(v=>v!==''));
      const parsed=[]; const errs=[];
      dataRows.forEach(({r:row, n})=>{
        const get=(...hs)=>{ for (const h of hs) { const i=col(h); if (i>=0 && String(row[i]??'').trim()!=='') return String(row[i]).trim(); } return ''; };
        const amount = parseFloat(get('amount').replace(/[^0-9.]/g,''));
        const typeKey = get('type').toLowerCase().replace(/[^a-z]/g,'');
        const category = get('category');
        const date = parseImportDate(get('date'));
        const company = (get('company')||'ARMSTECH').toUpperCase();
        if (!date)                 { errs.push(`Row ${n}: invalid date "${get('date')}" (use DD-MM-YYYY)`); return; }
        let type = IMPORT_IN.includes(typeKey) ? 'Cash In' : IMPORT_OUT.includes(typeKey) ? 'Cash Out' : '';
        if (!type)                 { errs.push(`Row ${n}: Type must be Cash In or Cash Out (found "${get('type')}")`); return; }
        if (!category)             { errs.push(`Row ${n}: Category is missing`); return; }
        if (isNaN(amount)||amount<=0) { errs.push(`Row ${n}: Amount must be a positive number`); return; }
        if (!COMPANIES.includes(company)) { errs.push(`Row ${n}: unknown company "${company}" (use ${COMPANIES.join(' / ')})`); return; }
        const modeRaw = get('payment mode','mode') || 'Cash';
        const entryByRaw = get('entry by','entry_by');
        parsed.push({
          _row: n,
          company, date, type,
          category: catMap.get(category.toLowerCase()) || category,
          payment_mode: modeMap.get(modeRaw.toLowerCase()) || modeRaw,
          amount, reference: get('reference','ref'),
          description: get('description','desc'),
          party_name: get('party') || null,
          entry_by: staffMap.get(entryByRaw.toLowerCase()) || entryByRaw,
          source: 'import',
        });
      });

      // Flag rows that already exist in the cash book (same company/date/type/amount/category/mode/reference)
      let dupCount = 0;
      if (parsed.length) {
        const minD = parsed.reduce((a,r)=>r.date<a?r.date:a, parsed[0].date);
        const maxD = parsed.reduce((a,r)=>r.date>a?r.date:a, parsed[0].date);
        const keyOf = r => [r.company, r.date, r.type, Number(r.amount).toFixed(2), String(r.category||'').toLowerCase(), String(r.payment_mode||'').toLowerCase(), String(r.reference||'').toLowerCase()].join('|');
        const existing = new Map();
        for (const c of [...new Set(parsed.map(r=>r.company))]) {
          const ex = await fetchAllRows(() => supabase.from('accounts_transactions').select('company,date,type,amount,category,payment_mode,reference').eq('company',c).gte('date',minD).lte('date',maxD).order('id'));
          ex.forEach(r => { const k = keyOf(r); existing.set(k, (existing.get(k)||0) + 1); });
        }
        parsed.forEach(r => { const k = keyOf(r); if ((existing.get(k)||0) > 0) { existing.set(k, existing.get(k)-1); r._dup = true; dupCount++; } });
      }

      setRows(parsed); setErrors(errs); setIncludeDups(false);
      setNewCats([...new Set(parsed.map(r=>r.category).filter(v=>v && !catMap.has(v.toLowerCase())))]);
      setNewModes([...new Set(parsed.map(r=>r.payment_mode).filter(v=>v && !modeMap.has(v.toLowerCase())))]);
      setUnknownEntryBy([...new Set(parsed.map(r=>r.entry_by).filter(v=>v && !staffMap.has(v.toLowerCase())))]);
      void dupCount;
      setStep('preview');
    } catch (err) {
      toast.error('Could not read the file: ' + (err.message || err));
    }
    setParsing(false);
    if (fileRef.current) fileRef.current.value='';
  }

  const dupRows = rows.filter(r=>r._dup);
  const toImport = rows.filter(r=>includeDups || !r._dup);

  async function runImport() {
    setImporting(true);
    let inserted=0; const errs=[];
    for (const c of [...new Set(toImport.map(r=>r.company))]) {
      if (!(await assertUnlocked(c, toImport.filter(r=>r.company===c).map(r=>r.date)))) { setImporting(false); return; }
    }
    // Create new categories / payment modes found in the file (staff are never auto-created)
    if (newCats.length) {
      const { error } = await supabase.from('accounts_categories').insert(newCats.map(name=>({name, type:'both', category_type:'General'})));
      if (error) errs.push('New categories: '+error.message);
    }
    if (newModes.length) {
      const { error } = await supabase.from('accounts_payment_modes').insert(newModes.map(name=>({name})));
      if (error) errs.push('New payment modes: '+error.message);
    }
    const clean = r => { const { _row, _dup, ...rest } = r; return { ...rest, reference: rest.reference || null, description: rest.description || null }; };
    for (let i=0;i<toImport.length;i+=50) {
      const batch = toImport.slice(i,i+50);
      const {error} = await supabase.from('accounts_transactions').insert(batch.map(clean));
      if (!error) { inserted += batch.length; continue; }
      // The batch failed as a whole - retry row by row so only the bad rows are lost and we can say which
      for (const r of batch) {
        const { error: e1 } = await supabase.from('accounts_transactions').insert(clean(r));
        if (e1) errs.push(`Row ${r._row}: ${e1.message}`); else inserted++;
      }
    }
    if (inserted > 0) logAudit(null, 'import', 'transaction', { company: toImport[0]?.company }, { summary: `Imported ${inserted} entries from Excel/CSV (${toImport.length - inserted} failed, ${includeDups ? 0 : dupRows.length} duplicates skipped)` });
    setResult({inserted,errors:errs,skippedDup: includeDups ? 0 : dupRows.length});
    setStep('done');
    setImporting(false);
    if (inserted>0) { toast.success(`✅ ${inserted} entries imported`); onSaved(); }
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:680,maxHeight:'90vh',overflowY:'auto'}}>
        <div className="modal-header">
          <span className="modal-title">📥 Import Transactions from Excel</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          {step==='upload' && (
            <div>
              <div style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:16,marginBottom:16}}>
                <div style={{fontWeight:700,marginBottom:8}}>Step 1 — Download Sample Format</div>
                <button className="btn btn-secondary" onClick={onSample}><Download size={13}/> Download Sample Excel</button>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>
                  Required columns: <strong>Date (DD-MM-YYYY), Type (Cash In/Cash Out), Category, Amount</strong><br/>
                  Optional: Company (default ARMSTECH), Payment Mode (default Cash), Party, Reference, Description, Entry By
                </div>
              </div>
              <div style={{border:'2px dashed var(--border)',borderRadius:10,padding:30,textAlign:'center',cursor:'pointer'}} onClick={()=>fileRef.current?.click()}>
                <Upload size={32} style={{color:'var(--accent)',marginBottom:8}}/>
                <div style={{fontWeight:600}}>{parsing?'Reading file...':'Click to upload Excel or CSV'}</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleFile}/>
              </div>
            </div>
          )}
          {step==='preview' && (
            <div>
              <div style={{display:'grid',gridTemplateColumns:(window.innerWidth<=768)?'1fr':'1fr 1fr',gap:10,marginBottom:16}}>
                <div style={{background:'var(--bg3)',padding:12,borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:11,color:'var(--text3)'}}>Valid Rows</div>
                  <div style={{fontSize:22,fontWeight:800,color:'var(--green)'}}>{rows.length}</div>
                </div>
                <div style={{background:'var(--bg3)',padding:12,borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:11,color:'var(--text3)'}}>Skipped (errors)</div>
                  <div style={{fontSize:22,fontWeight:800,color:'var(--red)'}}>{errors.length}</div>
                </div>
              </div>
              {errors.length>0&&<div style={{background:'#fef2f2',border:'1px solid var(--red)',borderRadius:8,padding:12,marginBottom:12,maxHeight:140,overflowY:'auto'}}>
                {errors.slice(0,15).map((e,i)=><div key={i} style={{fontSize:12,color:'var(--red)'}}>{e}</div>)}
                {errors.length>15&&<div style={{fontSize:12,color:'var(--red)'}}>...and {errors.length-15} more</div>}
              </div>}
              {dupRows.length>0&&<div style={{background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:'#1d4ed8'}}>
                <strong>{dupRows.length}</strong> row(s) look like entries that are already in the cash book (same date, type, amount, category, account and reference) and will be <strong>skipped</strong>.
                <label style={{display:'flex',alignItems:'center',gap:6,marginTop:6,cursor:'pointer'}}>
                  <input type="checkbox" checked={includeDups} onChange={e=>setIncludeDups(e.target.checked)}/> Import them anyway
                </label>
              </div>}
              {unknownEntryBy.length>0&&<div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:'#92400e'}}>
                👤 "Entry By" names not found in staff (saved as text only, no staff record is created): {unknownEntryBy.join(', ')}
              </div>}
              {(newCats.length>0||newModes.length>0)&&(
                <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#92400e',marginBottom:6}}>⚠️ New Master Data will be added on import (check for typos!):</div>
                  {newCats.length>0&&<div style={{fontSize:12,color:'#92400e'}}>📁 Categories: {newCats.join(', ')}</div>}
                  {newModes.length>0&&<div style={{fontSize:12,color:'#92400e'}}>💳 Payment Modes: {newModes.join(', ')}</div>}
                </div>
              )}
              <div className="table-wrap" style={{maxHeight:250,overflowY:'auto'}}>
                <table>
                  <thead><tr><th>Date</th><th>Company</th><th>Type</th><th>Category</th><th>Mode</th><th>Party</th><th style={{textAlign:'right'}}>Amount</th></tr></thead>
                  <tbody>
                    {rows.slice(0,10).map((r,i)=>(
                      <tr key={i}>
                        <td style={{fontSize:12}}>{fmtDMY(r.date)}</td>
                        <td><span className="badge badge-blue" style={{fontSize:9}}>{r.company}</span></td>
                        <td style={{color:r.type==='Cash In'?'var(--green)':'var(--red)',fontWeight:600,fontSize:12}}>{r.type}</td>
                        <td style={{fontSize:12}}>{r.category}</td>
                        <td style={{fontSize:11}}>{r.payment_mode}</td>
                        <td style={{fontSize:11}}>{r.party_name||'—'}{r._dup&&<span className="badge badge-amber" style={{fontSize:9,marginLeft:4}}>duplicate</span>}</td>
                        <td style={{textAlign:'right',fontWeight:600}}>{INR(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length>10&&<div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>...and {rows.length-10} more rows</div>}
            </div>
          )}
          {step==='done' && result && (
            <div style={{textAlign:'center',padding:20}}>
              <div style={{fontSize:48,marginBottom:8}}>✅</div>
              <div style={{fontWeight:800,fontSize:18,marginBottom:16}}>Import Complete!</div>
              <div style={{fontSize:14}}>Inserted: <strong>{result.inserted}</strong> entries</div>
              {result.skippedDup>0&&<div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>{result.skippedDup} duplicate row(s) skipped</div>}
              {result.errors.length>0&&<div style={{color:'var(--red)',fontSize:12,marginTop:8,textAlign:'left',maxHeight:140,overflowY:'auto'}}>
                {result.errors.slice(0,15).map((e,i)=><div key={i}>{e}</div>)}
                {result.errors.length>15&&<div>...and {result.errors.length-15} more</div>}
              </div>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {step==='upload'&&<button className="btn btn-secondary" onClick={onClose}>Cancel</button>}
          {step==='preview'&&<>
            <button className="btn btn-secondary" onClick={()=>setStep('upload')}>← Back</button>
            <button className="btn btn-primary" onClick={runImport} disabled={importing||toImport.length===0}>
              {importing?<><RefreshCw size={13}/> Importing...</>:`📥 Import ${toImport.length} Entries`}
            </button>
          </>}
          {step==='done'&&<button className="btn btn-primary" onClick={onClose}>Done ✅</button>}
        </div>
      </div>

      {/* ── Add Project Modal ── */}
    </div>
  );
}

// ══ FINANCIAL REPORT ══
function FinancialReport({ company, projectId }) {
  const [txns, setTxns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [selMonth, setSelMonth] = React.useState('');
  React.useEffect(()=>{ load(); },[company,projectId,year]);
  async function load() {
    setLoading(true);
    try {
      const data = await fetchAllRows(() => {
        let q = supabase.from('accounts_transactions').select('*').eq('company',company).gte('date',`${year}-01-01`).lte('date',`${year}-12-31`).order('date').order('id');
        if (projectId) q = q.eq('project_id',projectId);
        return q;
      });
      setTxns(data.filter(t => t.category !== TRANSFER_CATEGORY));   // moving money between own accounts is not income/expense
    } catch (e) { toast.error('Could not load report: ' + e.message); setTxns([]); }
    setLoading(false);
  }
  const months=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthlyData=months.map((m,i)=>{
    const mn=String(i+1).padStart(2,'0');
    const mt=txns.filter(t=>t.date?.startsWith(`${year}-${mn}`));
    const income=mt.filter(t=>t.type==='Cash In').reduce((a,t)=>a+Number(t.amount||0),0);
    const expense=mt.filter(t=>t.type==='Cash Out').reduce((a,t)=>a+Number(t.amount||0),0);
    return {month:m,mn,income,expense,net:income-expense,count:mt.length};
  });
  const totalIncome=monthlyData.reduce((a,m)=>a+m.income,0);
  const totalExpense=monthlyData.reduce((a,m)=>a+m.expense,0);
  const totalNet=totalIncome-totalExpense;
  const displayTxns=selMonth?txns.filter(t=>t.date?.startsWith(`${year}-${selMonth}`)):txns;
  const catBreakdown={};
  displayTxns.forEach(t=>{const k=`${t.type}::${t.category||'Uncategorized'}`;if(!catBreakdown[k])catBreakdown[k]={type:t.type,category:t.category||'Uncategorized',amount:0,count:0};catBreakdown[k].amount+=Number(t.amount||0);catBreakdown[k].count++;});
  const categories=Object.values(catBreakdown).sort((a,b)=>b.amount-a.amount);
  function exportExcel() {
    const monthlyRows = monthlyData.map(m=>({'Month':`${m.month} ${year}`,'Income':m.income,'Expense':m.expense,'Net P&L':m.net,'Transactions':m.count}));
    monthlyRows.push({'Month':'TOTAL','Income':totalIncome,'Expense':totalExpense,'Net P&L':totalNet,'Transactions':monthlyData.reduce((a,m)=>a+m.count,0)});
    saveStyledXlsx(`FinancialReport_${company}_${year}${selMonth?'_'+selMonth:''}.xlsx`, [
      { name:'Monthly', title:`Financial Report ${year} — ${company}`, rows:monthlyRows },
      { name:selMonth ? `Categories ${months[Number(selMonth)-1]}` : 'Categories', title:'Category-wise Totals', rows:categories.map(c=>({'Type':c.type,'Category':c.category,'Amount':c.amount,'Transactions':c.count})) },
    ]);
  }
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <select value={year} onChange={e=>setYear(Number(e.target.value))} style={{width:90}}>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}</select>
        <select value={selMonth} onChange={e=>setSelMonth(e.target.value)} style={{width:100}}><option value="">All Months</option>{months.map((m,i)=><option key={m} value={String(i+1).padStart(2,'0')}>{m}</option>)}</select>
        {selMonth&&<button className="btn btn-ghost btn-sm" onClick={()=>setSelMonth('')}>✕ Clear</button>}
        <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto'}} onClick={exportExcel}><Download size={14}/> Export Excel</button>
        <span style={{fontSize:11,color:'var(--text3)'}}>Internal transfers between accounts are not counted as income or expense.</span>
      </div>
      {loading?<div className="loading"><div className="spinner"/></div>:(
        <>
          <div className="stats-grid" style={{marginBottom:16}}>
            {[{label:'Total Income',val:`₹${totalIncome.toLocaleString('en-IN')}`,color:'var(--green)'},{label:'Total Expense',val:`₹${totalExpense.toLocaleString('en-IN')}`,color:'var(--red)'},{label:'Net P&L',val:`₹${totalNet.toLocaleString('en-IN')}`,color:totalNet>=0?'var(--green)':'var(--red)'},{label:'Transactions',val:displayTxns.length,color:'var(--accent)'}].map(s=><div key={s.label} className="stat-card" style={{padding:12,textAlign:'center'}}><div style={{fontSize:18,fontWeight:800,color:s.color}}>{s.val}</div><div style={{fontSize:11,color:'var(--text3)'}}>{s.label}</div></div>)}
          </div>
          {!selMonth&&<div className="card" style={{marginBottom:16,overflowX:'auto'}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>📅 Monthly Breakdown — {year}</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'var(--accent-light)'}}>{['Month','Income','Expense','Net P&L','Txns'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>)}</tr></thead>
              <tbody>{monthlyData.map(m=><tr key={m.month} style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}} onClick={()=>setSelMonth(m.mn)}>
                <td style={{padding:'7px 10px',fontWeight:600}}>{m.month}</td>
                <td style={{padding:'7px 10px',color:'var(--green)',fontWeight:600}}>₹{m.income.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px',color:'var(--red)',fontWeight:600}}>₹{m.expense.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px',fontWeight:700,color:m.net>=0?'var(--green)':'var(--red)'}}>₹{m.net.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px',color:'var(--text3)'}}>{m.count}</td>
              </tr>)}</tbody>
              <tfoot><tr style={{background:'var(--surface2)',fontWeight:700}}>
                <td style={{padding:'7px 10px'}}>Total</td>
                <td style={{padding:'7px 10px',color:'var(--green)'}}>₹{totalIncome.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px',color:'var(--red)'}}>₹{totalExpense.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px',color:totalNet>=0?'var(--green)':'var(--red)'}}>₹{totalNet.toLocaleString('en-IN')}</td>
                <td style={{padding:'7px 10px'}}>{txns.length}</td>
              </tr></tfoot>
            </table>
          </div>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            {['income','expense'].map(type=><div key={type} className="card">
              <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:type==='income'?'var(--green)':'var(--red)'}}>{type==='income'?'💚 Cash In (Income)':'🔴 Cash Out (Expense)'} by Category</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'var(--surface2)'}}><th style={{padding:'5px 8px',textAlign:'left'}}>Category</th><th style={{padding:'5px 8px',textAlign:'right'}}>Amount</th><th style={{padding:'5px 8px',textAlign:'center'}}>Txns</th></tr></thead>
                <tbody>{categories.filter(c=>c.type===(type==='income'?'Cash In':'Cash Out')).map(c=><tr key={c.category} style={{borderBottom:'1px solid var(--border)'}}><td style={{padding:'5px 8px'}}>{c.category}</td><td style={{padding:'5px 8px',textAlign:'right',fontWeight:600}}>₹{c.amount.toLocaleString('en-IN')}</td><td style={{padding:'5px 8px',textAlign:'center',color:'var(--text3)'}}>{c.count}</td></tr>)}</tbody>
              </table>
            </div>)}
          </div>
        </>
      )}
    </div>
  );
}

// ══ PARTY LEDGER ══
function PartyLedger({ company, projectId }) {
  const [summaryRows, setSummaryRows] = React.useState([]);   // one row per party: {name,count,income,expense,last}
  const [loading, setLoading] = React.useState(true);
  const [selParty, setSelParty] = React.useState('');
  const [partyTxns, setPartyTxns] = React.useState([]);
  const [partyLoading, setPartyLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');

  React.useEffect(()=>{ setSelParty(''); setPartyTxns([]); load(); },[company,projectId]);
  async function load() {
    setLoading(true);
    try {
      // Fast path: let the database add everything up (needs the accounts_party_summary function - see the SQL)
      const rpc = await supabase.rpc('accounts_party_summary', { p_company: company, p_project_id: projectId ? String(projectId) : null });
      if (!rpc.error) {
        setSummaryRows((rpc.data||[]).map(r=>({ name:r.party_name, count:Number(r.txn_count||0), income:Number(r.income||0), expense:Number(r.expense||0), last:r.last_date })));
      } else {
        // Fallback: download only the four columns needed, then add up here
        const rows = await fetchAllRows(() => {
          let q = supabase.from('accounts_transactions').select('party_name,type,amount,date').eq('company',company).not('party_name','is',null).order('id');
          if (projectId) q = q.eq('project_id',projectId);
          return q;
        });
        const m = new Map();
        rows.forEach(t => {
          if (!t.party_name) return;
          const e = m.get(t.party_name) || { name:t.party_name, count:0, income:0, expense:0, last:'' };
          e.count++;
          if (t.type==='Cash In') e.income += Number(t.amount||0); else if (t.type==='Cash Out') e.expense += Number(t.amount||0);
          if (t.date > e.last) e.last = t.date;
          m.set(t.party_name, e);
        });
        setSummaryRows([...m.values()]);
      }
    } catch (e) { toast.error('Could not load party ledger: ' + e.message); setSummaryRows([]); }
    setLoading(false);
  }

  // Full transaction list only for the party that was clicked
  React.useEffect(()=>{
    if (!selParty) { setPartyTxns([]); return; }
    let dead = false;
    setPartyLoading(true);
    fetchAllRows(() => {
      let q = supabase.from('accounts_transactions').select('*').eq('company',company).eq('party_name',selParty).order('date',{ascending:false}).order('id',{ascending:false});
      if (projectId) q = q.eq('project_id',projectId);
      return q;
    }).then(rows => { if (!dead) { setPartyTxns(rows); setPartyLoading(false); } })
      .catch(e => { if (!dead) { toast.error('Could not load this party: ' + e.message); setPartyLoading(false); } });
    return () => { dead = true; };
  },[selParty, company, projectId]);

  const partySummary = summaryRows
    .map(p=>({ ...p, balance: p.income - p.expense }))
    .filter(p=>!search||p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>a.name.localeCompare(b.name));
  const partyIncome=partyTxns.filter(t=>t.type==='Cash In').reduce((a,t)=>a+Number(t.amount||0),0);
  const partyExpense=partyTxns.filter(t=>t.type==='Cash Out').reduce((a,t)=>a+Number(t.amount||0),0);
  function exportExcel() {
    const sheets = [{ name:'Parties', title:`Party Summary — ${company}`, rows:partySummary.map(p=>({'Party':p.name,'Cash In':p.income,'Cash Out':p.expense,'Net':p.balance,'Transactions':p.count,'Last Date':p.last||''})) }];
    if (selParty) {
      const asc = [...partyTxns].sort((a,b)=>String(a.date).localeCompare(String(b.date)) || String(a.created_at||'').localeCompare(String(b.created_at||'')));
      let bal = 0;
      const rows = asc.map(t=>{ const amt = Number(t.amount||0); bal += t.type==='Cash In' ? amt : -amt;
        return { 'Date':t.date,'Description':t.description||'','Category':t.category||'','Mode':t.payment_mode||'','Reference':t.reference||'','Cash In':t.type==='Cash In'?amt:0,'Cash Out':t.type==='Cash Out'?amt:0,'Running Balance':bal }; });
      sheets.push({ name:selParty.replace(/[\\/?*[\]:]/g,' ').slice(0,31) || 'Statement', title:`Statement — ${selParty}`, rows });
    }
    saveStyledXlsx(`PartyLedger_${company}${selParty?'_'+selParty.replace(/[^a-zA-Z0-9]+/g,'_'):''}.xlsx`, sheets);
  }
  return (
    <div style={{display:'grid',gridTemplateColumns:selParty?'230px 1fr':'1fr',gap:16}}>
      <div>
        <div style={{display:'flex',gap:8,marginBottom:8}}>
          <div className="search-bar" style={{flex:1}}><Search size={14}/><input placeholder="Search party..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          {selParty&&<button className="btn btn-ghost btn-sm" onClick={()=>setSelParty('')}>✕</button>}
          <button className="btn btn-secondary btn-sm" onClick={exportExcel} title={selParty?'Export all parties + this party statement':'Export all parties'}><Download size={14}/></button>
        </div>
        {loading?<div className="loading"><div className="spinner"/></div>:<div style={{maxHeight:'70vh',overflowY:'auto'}}>
          {partySummary.map(p=><div key={p.name} className="card" style={{marginBottom:8,cursor:'pointer',border:`1px solid ${selParty===p.name?'var(--accent)':'var(--border)'}`,padding:10}} onClick={()=>setSelParty(p.name)}>
            <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>{p.name}</div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:11}}>
              <span style={{color:'var(--green)'}}>+₹{p.income.toLocaleString('en-IN')}</span>
              <span style={{color:'var(--red)'}}>-₹{p.expense.toLocaleString('en-IN')}</span>
            </div>
            <div style={{fontWeight:700,fontSize:12,color:p.balance>=0?'var(--green)':'var(--red)',marginTop:4}}>Net: ₹{p.balance.toLocaleString('en-IN')}</div>
            <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>{p.count} txns · Last: {p.last||'—'}</div>
          </div>)}
          {partySummary.length===0&&<div style={{color:'var(--text3)',fontSize:13,padding:16,textAlign:'center'}}>No parties found</div>}
        </div>}
      </div>
      {selParty&&<div>
        <div style={{fontWeight:700,fontSize:16,marginBottom:12}}>{selParty}</div>
        <div className="stats-grid" style={{marginBottom:12}}>
          {[{label:'Total Income',val:`₹${partyIncome.toLocaleString('en-IN')}`,color:'var(--green)'},{label:'Total Expense',val:`₹${partyExpense.toLocaleString('en-IN')}`,color:'var(--red)'},{label:'Net Balance',val:`₹${(partyIncome-partyExpense).toLocaleString('en-IN')}`,color:(partyIncome-partyExpense)>=0?'var(--green)':'var(--red)'},{label:'Transactions',val:partyTxns.length,color:'var(--accent)'}].map(s=><div key={s.label} className="stat-card" style={{padding:10,textAlign:'center'}}><div style={{fontSize:16,fontWeight:800,color:s.color}}>{s.val}</div><div style={{fontSize:10,color:'var(--text3)'}}>{s.label}</div></div>)}
        </div>
        {partyLoading && <div className="loading"><div className="spinner"/></div>}
        <div className="table-wrap"><table style={{fontSize:12}}>
          <thead><tr style={{background:'var(--accent-light)'}}><th>Date</th><th>Description</th><th>Category</th><th>Type</th><th>Amount</th><th>Mode</th></tr></thead>
          <tbody>{partyTxns.map(t=><tr key={t.id} style={{borderBottom:'1px solid var(--border)'}}>
            <td style={{padding:'6px 10px'}}>{fmtDMY(t.date)}</td>
            <td style={{padding:'6px 10px',fontWeight:500}}>{t.description||'—'}</td>
            <td style={{padding:'6px 10px'}}>{t.category||'—'}</td>
            <td style={{padding:'6px 10px'}}><span className={`badge ${t.type==='Cash In'?'badge-green':'badge-red'}`}>{t.type}</span></td>
            <td style={{padding:'6px 10px',fontWeight:600,color:t.type==='Cash In'?'var(--green)':'var(--red)'}}>₹{(t.amount||0).toLocaleString('en-IN')}</td>
            <td style={{padding:'6px 10px'}}>{t.payment_mode||'—'}</td>
          </tr>)}</tbody>
        </table></div>
      </div>}
    </div>
  );
}

// ══ PAYMENT REMINDERS & RECURRING ══
function PaymentReminders({
  isEditor, company, user }) {
  const { canEdit } = useAuth();
  const [reminders, setReminders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [subTab, setSubTab] = React.useState('payable');
  const emptyForm={title:'',amount:0,reminder_date:'',repeat_type:'once',category:'',party_name:'',type:'payable',notes:'',status:'pending',notify_whatsapp:false,whatsapp_numbers:''};
  const [form, setForm] = React.useState(emptyForm);
  const [payFor, setPayFor] = React.useState(null);
  const [payForm, setPayForm] = React.useState({ date: todayStr(), mode: 'Cash', post: true });
  const [modeList, setModeList] = React.useState([]);
  React.useEffect(()=>{ supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setModeList((data||[]).map(m=>m.name))); },[]);

  async function markPaid() {
    const r = payFor;
    setSaving(true);
    let posted = false;
    try {
      if (payForm.post && Number(r.amount) > 0) {
        if (!(await assertUnlocked(company, [payForm.date]))) { setSaving(false); return; }
        const { error } = await supabase.from('accounts_transactions').insert({
          company, date: payForm.date, type: r.type === 'receivable' ? 'Cash In' : 'Cash Out',
          category: r.category || 'Miscellaneous', payment_mode: payForm.mode, party_name: r.party_name || null,
          amount: Number(r.amount), description: r.title + (r.notes ? ' - ' + r.notes : ''),
          source: 'reminder', entry_by: user?.email || null,
        });
        if (error) throw error;
        posted = true;
      }
      const { error: uErr } = await supabase.from('payment_reminders').update({ status: 'done' }).eq('id', r.id);
      if (uErr) throw new Error((posted ? 'The cash-book entry was posted, but the reminder could not be marked done (do NOT post it again): ' : '') + uErr.message);
      // recurring: queue the next occurrence
      if (r.repeat_type && r.repeat_type !== 'once' && r.reminder_date) {
        const step = { monthly: 1, quarterly: 3, yearly: 12 }[r.repeat_type] || 1;
        const next = { ...r }; delete next.id; delete next.created_at;
        next.reminder_date = addMonthsStr(r.reminder_date, step); next.status = 'pending';
        const { error: nErr } = await supabase.from('payment_reminders').insert(next);
        if (nErr) toast.error('Marked done, but the next occurrence could not be created: ' + nErr.message);
      }
      logAudit(user, 'reminder_paid', 'payment_reminder', { id: r.id, company }, { summary: `${r.title} marked done${posted ? ' and posted to the cash book' : ''}` });
      toast.success(posted ? 'Marked done and posted to the cash book' : 'Marked done');
      setPayFor(null); load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  React.useEffect(()=>{ load(); },[company]);
  async function load() {
    setLoading(true);
    const {data}=await supabase.from('payment_reminders').select('*').eq('project',company).order('reminder_date');
    setReminders(data||[]); setLoading(false);
  }
  async function save() {
    if (!form.title||!form.reminder_date) { toast.error('Title and date required'); return; }
    setSaving(true);
    const payload={...form,amount:Number(form.amount)||0,project:company};
    if (!editing) payload.created_by = user?.email || '';   // keep the original creator when editing
    const {error}=editing?await supabase.from('payment_reminders').update(payload).eq('id',editing):await supabase.from('payment_reminders').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Saved!'); setShowModal(false); setEditing(null); setForm(emptyForm); load(); }
    setSaving(false);
  }

  const today=todayStr();
  const oneWeek=dateStrPlus(7);

  // Split by sub-tab
  const payable   = reminders.filter(r=>r.type==='payable'  &&r.repeat_type==='once');
  const receivable= reminders.filter(r=>r.type==='receivable'&&r.repeat_type==='once');
  const recurring = reminders.filter(r=>r.repeat_type!=='once');

  // Overdue/due soon across all
  const overdue  = reminders.filter(r=>r.reminder_date&&r.reminder_date<today&&r.status==='pending');
  const dueSoon  = reminders.filter(r=>r.reminder_date>=today&&r.reminder_date<=oneWeek&&r.status==='pending');

  const display = subTab==='payable'?payable:subTab==='receivable'?receivable:recurring;

  // Summary amounts
  const totalPayable   = payable.filter(r=>r.status==='pending').reduce((a,r)=>a+(r.amount||0),0);
  const totalReceivable= receivable.filter(r=>r.status==='pending').reduce((a,r)=>a+(r.amount||0),0);

  function renderTable(items) {
    return (
      <div className="table-wrap"><table style={{fontSize:12}}>
        <thead><tr style={{background:'var(--accent-light)'}}>
          <th>#</th><th>Title</th><th>Party</th><th>Amount</th><th>Due Date</th>
          {subTab==='recurring'&&<th>Repeat</th>}
          <th>Category</th><th>Status</th><th>Notes</th>
          {canEdit('accounts')&&<th>Actions</th>}
        </tr></thead>
        <tbody>{items.map((r,i)=>(
          <tr key={r.id} style={{borderBottom:'1px solid var(--border)',
            background:r.reminder_date&&r.reminder_date<today&&r.status==='pending'?'rgba(239,68,68,0.04)':
            r.reminder_date&&r.reminder_date<=oneWeek&&r.status==='pending'?'rgba(245,158,11,0.04)':undefined}}>
            <td style={{padding:'6px 8px',color:'var(--text3)'}}>{i+1}</td>
            <td style={{padding:'6px 8px',fontWeight:500}}>{r.title}</td>
            <td style={{padding:'6px 8px'}}>{r.party_name||'—'}</td>
            <td style={{padding:'6px 8px',fontWeight:600}}>{r.amount?`₹${Number(r.amount).toLocaleString('en-IN')}`:'-'}</td>
            <td style={{padding:'6px 8px',color:r.reminder_date&&r.reminder_date<today&&r.status==='pending'?'var(--red)':r.reminder_date&&r.reminder_date<=oneWeek&&r.status==='pending'?'var(--amber)':'inherit'}}>{fmtDMY(r.reminder_date)||'—'}</td>
            {subTab==='recurring'&&<td style={{padding:'6px 8px'}}><span className="badge badge-blue">{r.repeat_type}</span></td>}
            <td style={{padding:'6px 8px'}}>{r.category||'—'}</td>
            <td style={{padding:'6px 8px'}}><span className={`badge ${r.status==='done'?'badge-green':r.status==='dismissed'?'badge-gray':'badge-amber'}`}>{r.status}</span></td>
            <td style={{padding:'6px 8px',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis'}}>{r.notes||'—'}</td>
            {canEdit('accounts')&&<td style={{padding:'6px 8px'}}><div style={{display:'flex',gap:4}}>
              {r.status==='pending'&&<button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--green)'}} title="Mark paid / received" onClick={()=>{setPayForm({date:todayStr(),mode:modeList.includes('Cash')?'Cash':(modeList[0]||'Cash'),post:Number(r.amount)>0});setPayFor(r);}}>✓</button>}
              {r.status==='pending'&&<button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--text3)'}} onClick={async()=>{await supabase.from('payment_reminders').update({status:'dismissed'}).eq('id',r.id);load();}}>✕</button>}
              <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(r.id);setForm({...r});setShowModal(true);}}><Edit2 size={12}/></button>
              <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete?'))return;await supabase.from('payment_reminders').delete().eq('id',r.id);load();}}><Trash2 size={12}/></button>
            </div></td>}
          </tr>
        ))}</tbody>
      </table></div>
    );
  }

  return (
    <div>
      {/* Summary stats */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          {label:'Payable (we owe)',val:`₹${totalPayable.toLocaleString('en-IN')}`,color:'var(--red)'},
          {label:'Receivable (owed to us)',val:`₹${totalReceivable.toLocaleString('en-IN')}`,color:'var(--green)'},
          {label:'Overdue',val:overdue.length,color:'var(--red)'},
          {label:'Due This Week',val:dueSoon.length,color:'var(--amber)'},
          {label:'Recurring',val:recurring.length,color:'var(--blue)'},
        ].map(s=><div key={s.label} className="stat-card" style={{padding:10,textAlign:'center'}}>
          <div style={{fontSize:16,fontWeight:800,color:s.color}}>{s.val}</div>
          <div style={{fontSize:10,color:'var(--text3)'}}>{s.label}</div>
        </div>)}
      </div>

      {/* Overdue/due soon alert */}
      {(overdue.length>0||dueSoon.length>0)&&(
        <div style={{marginBottom:16,padding:12,background:'rgba(239,68,68,0.08)',borderRadius:8,border:'1px solid rgba(239,68,68,0.2)'}}>
          <div style={{fontWeight:700,color:'var(--red)',marginBottom:6}}>⚠️ Attention Required</div>
          {[...overdue,...dueSoon].map(r=>(
            <div key={r.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 0',borderBottom:'1px solid rgba(239,68,68,0.1)',fontSize:13}}>
              <span>
                <span className={`badge ${r.type==='receivable'?'badge-green':'badge-red'}`} style={{marginRight:6,fontSize:9}}>{r.type}</span>
                <strong>{r.title}</strong>{r.party_name?` — ${r.party_name}`:''}
              </span>
              <span style={{color:r.reminder_date<today?'var(--red)':'var(--amber)',fontWeight:600}}>
                {fmtDMY(r.reminder_date)}{r.amount?` · ₹${Number(r.amount).toLocaleString('en-IN')}`:''}{r.reminder_date<today?' (OVERDUE)':' (due soon)'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sub-tabs + Add button */}
      <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:4}}>
          <button className={`btn btn-sm ${subTab==='payable'?'btn-primary':'btn-ghost'}`} onClick={()=>setSubTab('payable')}>
            💸 Payable ({payable.filter(r=>r.status==='pending').length})
          </button>
          <button className={`btn btn-sm ${subTab==='receivable'?'btn-primary':'btn-ghost'}`} onClick={()=>setSubTab('receivable')}>
            💰 Receivable ({receivable.filter(r=>r.status==='pending').length})
          </button>
          <button className={`btn btn-sm ${subTab==='recurring'?'btn-primary':'btn-ghost'}`} onClick={()=>setSubTab('recurring')}>
            🔄 Recurring ({recurring.length})
          </button>
        </div>
        {canEdit('accounts')&&<button className="btn btn-primary btn-sm" style={{marginLeft:'auto'}}
          onClick={()=>{setEditing(null);setForm({...emptyForm,type:subTab==='recurring'?'payable':subTab,repeat_type:subTab==='recurring'?'monthly':'once'});setShowModal(true);}}>
          <Plus size={13}/> Add
        </button>}
      </div>

      {loading?<div className="loading"><div className="spinner"/></div>:(
        display.length===0
          ?<div className="empty-state"><p>No {subTab==='payable'?'payable':subTab==='receivable'?'receivable':'recurring'} entries yet. Click + Add to create one.</p></div>
          :renderTable(display)
      )}

      {payFor&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setPayFor(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header"><span className="modal-title">{payFor.type==='receivable'?'Mark as received':'Mark as paid'}</span><button className="btn btn-ghost btn-sm" onClick={()=>setPayFor(null)}>✕</button></div>
            <div className="modal-body"><div className="form-grid">
              <div className="form-group full"><div style={{fontSize:13,fontWeight:600}}>{payFor.title}{payFor.party_name?` — ${payFor.party_name}`:''}</div>
                <div style={{fontSize:12,color:'var(--text3)'}}>{payFor.amount?`₹${Number(payFor.amount).toLocaleString('en-IN')}`:'No amount'}{payFor.repeat_type!=='once'?` · repeats ${payFor.repeat_type} (next one will be created)`:''}</div></div>
              <div className="form-group full"><label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                <input type="checkbox" checked={payForm.post} disabled={!(Number(payFor.amount)>0)} onChange={e=>setPayForm(f=>({...f,post:e.target.checked}))}/> Also post to the cash book ({payFor.type==='receivable'?'Cash In':'Cash Out'})</label></div>
              {payForm.post&&<>
                <div className="form-group"><label>Date</label><DateInput value={payForm.date} onChange={e=>setPayForm(f=>({...f,date:e.target.value}))}/></div>
                <div className="form-group"><label>Account</label><select value={payForm.mode} onChange={e=>setPayForm(f=>({...f,mode:e.target.value}))}>{(modeList.length?modeList:['Cash']).map(m=><option key={m} value={m}>{m}</option>)}</select></div>
              </>}
            </div></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setPayFor(null)}>Cancel</button><button className="btn btn-primary" onClick={markPaid} disabled={saving}>{saving?'Saving...':'Confirm'}</button></div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} {form.repeat_type!=='once'?'Recurring Transaction':form.type==='receivable'?'Receivable':'Payable'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full"><label>Title *</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Student loan return, Monthly rent, Vendor payment"/></div>
                <div className="form-group"><label>Type</label>
                  <select value={form.type||'payable'} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                    <option value="payable">💸 Payable (we owe)</option>
                    <option value="receivable">💰 Receivable (owed to us)</option>
                  </select>
                </div>
                <div className="form-group"><label>Repeat</label>
                  <select value={form.repeat_type||'once'} onChange={e=>setForm(f=>({...f,repeat_type:e.target.value}))}>
                    <option value="once">One-time</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group"><label>Party Name</label><input value={form.party_name||''} onChange={e=>setForm(f=>({...f,party_name:e.target.value}))} placeholder="Person/vendor/student name"/></div>
                <div className="form-group"><label>Amount (₹)</label><input type="number" value={form.amount||''} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}/></div>
                <div className="form-group"><label>Due Date *</label><DateInput value={form.reminder_date||''} onChange={e=>setForm(f=>({...f,reminder_date:e.target.value}))}/></div>
                <div className="form-group"><label>Category</label><input value={form.category||''} onChange={e=>setForm(f=>({...f,category:e.target.value}))} placeholder="Rent, Salary, Loan, Hostel advance"/></div>
                <div className="form-group full"><label>Notes</label><textarea rows={2} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
                <div className="form-group full">
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                    <input type="checkbox" checked={form.notify_whatsapp||false} onChange={e=>setForm(f=>({...f,notify_whatsapp:e.target.checked}))}/>
                    Send WhatsApp notification when due
                  </label>
                  <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>Saved with the reminder. Messages are sent by your reminder job, not from this page.</div>
                  {form.notify_whatsapp&&<input style={{marginTop:6}} value={form.whatsapp_numbers||''} onChange={e=>setForm(f=>({...f,whatsapp_numbers:e.target.value}))} placeholder="WhatsApp numbers comma separated (e.g. 9995001234, 9995005678)"/>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══ BANK RECONCILIATION ══
function BankReconciliation({
  isEditor, company }) {
  const { canEdit } = useAuth();
  const [statements, setStatements] = React.useState([]);
  const [txns, setTxns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selStatement, setSelStatement] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const emptyForm={bank_name:'',account_number:'',statement_date:'',opening_balance:0,closing_balance:0};
  const [form, setForm] = React.useState(emptyForm);
  const [bankEntries, setBankEntries] = React.useState([{date:'',description:'',amount:0,type:'credit'}]);
  const [acctFilter, setAcctFilter] = React.useState('');   // compare the statement against ONE book account (e.g. the matching bank)
  const [modeList, setModeList] = React.useState([]);
  const stmtFileRef = React.useRef();
  React.useEffect(()=>{ supabase.from('accounts_payment_modes').select('name').order('name').then(({data})=>setModeList((data||[]).map(m=>m.name))); },[]);
  React.useEffect(()=>{ load(); },[company]);

  async function deleteStatement(st) {
    if (!window.confirm(`Delete the ${st.bank_name} statement dated ${st.statement_date}? Your cash book entries are not affected.`)) return;
    const { error } = await supabase.from('bank_statements').delete().eq('id', st.id);
    if (error) { toast.error('Delete failed: ' + error.message); return; }
    toast.success('Statement deleted'); setSelStatement(null); load();
  }

  // Read statement rows from an Excel/CSV export: Date + Description + (Debit/Credit columns, or Amount + Type)
  async function loadStatementFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '' });
      // find the header row (statements often have a few title lines first)
      const hi = raw.findIndex(r => r.some(c => /date/i.test(String(c))) && r.some(c => /(debit|withdraw|credit|deposit|amount)/i.test(String(c))));
      if (hi < 0) { toast.error('Could not find a header row with Date and Debit/Credit or Amount columns'); return; }
      const hd = raw[hi].map(h => String(h).trim().toLowerCase());
      const ix = (...keys) => hd.findIndex(h => keys.some(k => h.includes(k)));
      const iDate = ix('date'), iDesc = ix('narration','particular','description','remark','details'), iDeb = ix('debit','withdraw'), iCr = ix('credit','deposit'), iAmt = ix('amount'), iType = ix('type','dr/cr');
      const num = v => parseFloat(String(v).replace(/[^0-9.\-]/g,''));
      const out = []; let skipped = 0;
      raw.slice(hi + 1).forEach(row => {
        const date = parseImportDate(row[iDate]);
        if (!date) { if (row.some(v => v !== '')) skipped++; return; }
        let amount = 0, type = 'credit';
        if (iDeb >= 0 && num(row[iDeb]) > 0) { amount = num(row[iDeb]); type = 'debit'; }
        else if (iCr >= 0 && num(row[iCr]) > 0) { amount = num(row[iCr]); type = 'credit'; }
        else if (iAmt >= 0 && !isNaN(num(row[iAmt])) && num(row[iAmt]) !== 0) { amount = Math.abs(num(row[iAmt])); type = (/dr|debit|out/i.test(String(row[iType] ?? '')) || num(row[iAmt]) < 0) ? 'debit' : 'credit'; }
        else { skipped++; return; }
        out.push({ date, description: String(row[iDesc] ?? '').trim() || '(no description)', amount, type });
      });
      if (!out.length) { toast.error('No usable rows found'); return; }
      setBankEntries(out);
      toast.success(`Loaded ${out.length} rows${skipped ? ` (${skipped} skipped)` : ''}`);
    } catch (err) { toast.error('Could not read the file: ' + err.message); }
    if (stmtFileRef.current) stmtFileRef.current.value = '';
  }
  const [txnsLoading, setTxnsLoading] = React.useState(false);
  const [bookBal, setBookBal] = React.useState(null);
  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('bank_statements').select('*').eq('project',company).order('statement_date',{ascending:false});
      if (error) throw error;
      const sts = data || [];
      setStatements(sts);
      setLoading(false);          // show the statement list straight away
      // Book entries are needed only for the dates the statements cover
      const ds = sts.flatMap(st => [st.statement_date, ...((Array.isArray(st.entries)?st.entries:[]).map(e=>e.date))]).filter(Boolean).sort();
      if (!ds.length) { setTxns([]); return; }
      setTxnsLoading(true);
      const t = await fetchAllRows(() => supabase.from('accounts_transactions').select('id,date,type,amount,description,payment_mode').eq('company',company).gte('date',ds[0]).lte('date',ds[ds.length-1]).order('date',{ascending:false}).order('id',{ascending:false}));
      setTxns(t);
    } catch (e) { toast.error('Could not load reconciliation data: ' + e.message); }
    setLoading(false); setTxnsLoading(false);
  }

  // Book balance of ONE account up to the statement date (only fetched when an account is chosen to compare)
  React.useEffect(()=>{
    if (!selStatement || !acctFilter) { setBookBal(null); return; }
    let dead = false;
    setBookBal(undefined);
    fetchAllRows(() => supabase.from('accounts_transactions').select('type,amount').eq('company',company).eq('payment_mode',acctFilter).lte('date',selStatement.statement_date).order('id'))
      .then(rows => { if (!dead) setBookBal(rows.reduce((a,t)=>a+(t.type==='Cash In'?1:t.type==='Cash Out'?-1:0)*Number(t.amount||0),0)); })
      .catch(e => { if (!dead) { setBookBal(null); toast.error('Could not work out the book balance: ' + e.message); } });
    return () => { dead = true; };
  },[selStatement, acctFilter, company]);

  async function saveStatement() {
    if (!form.bank_name||!form.statement_date) { toast.error('Bank name and date required'); return; }
    setSaving(true);
    const payload={...form,opening_balance:Number(form.opening_balance)||0,closing_balance:Number(form.closing_balance)||0,entries:bankEntries.filter(e=>e.description),project:company};
    const {error}=await supabase.from('bank_statements').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Statement saved!'); setShowModal(false); setForm(emptyForm); setBankEntries([{date:'',description:'',amount:0,type:'credit'}]); load(); }
    setSaving(false);
  }
  function getMatches(stmt) {
    const entries=Array.isArray(stmt?.entries)?stmt.entries:[];
    const matched=[],unmatched=[];
    const pool = acctFilter ? txns.filter(t=>t.payment_mode===acctFilter) : txns;
    const used = new Set();   // each book entry can match only ONE bank row
    entries.forEach(e=>{
      const wantType = e.type==='credit' ? 'Cash In' : 'Cash Out';
      const match=pool.find(t=>!used.has(t.id)&&t.date===e.date&&t.type===wantType&&Math.abs(Number(t.amount||0)-Math.abs(e.amount||0))<1);
      if (match) { used.add(match.id); matched.push({bank:e,book:match}); } else unmatched.push({bank:e});
    });
    // book entries inside the statement's date range that the bank never showed (only meaningful once one account is chosen)
    let bookOnly = [];
    if (acctFilter && entries.length) {
      const ds = entries.map(e=>e.date).filter(Boolean).sort();
      bookOnly = pool.filter(t => !used.has(t.id) && t.date >= ds[0] && t.date <= ds[ds.length-1]);
    }
    return {matched,unmatched,bookOnly};
  }
  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        {canEdit('accounts')&&<button className="btn btn-primary" onClick={()=>{setShowModal(true);setForm(emptyForm);setBankEntries([{date:'',description:'',amount:0,type:'credit'}]);}}>+ Upload Statement</button>}
      </div>
      {loading?<div className="loading"><div className="spinner"/></div>:(
        <div style={{display:'grid',gridTemplateColumns:selStatement?'220px 1fr':'1fr',gap:16}}>
          <div>
            {statements.length===0?<div className="empty-state"><p>No bank statements uploaded yet</p></div>:statements.map(s=>{
              const {matched,unmatched}=getMatches(s);
              return <div key={s.id} className="card" style={{marginBottom:8,cursor:'pointer',border:`1px solid ${selStatement?.id===s.id?'var(--accent)':'var(--border)'}`,padding:10}} onClick={()=>setSelStatement(s)}>
                <div style={{fontWeight:700}}>{s.bank_name}</div>
                <div style={{fontSize:12,color:'var(--text3)'}}>{fmtDMY(s.statement_date)} · A/C: {s.account_number||'—'}</div>
                <div style={{fontSize:12,marginTop:4}}>{txnsLoading?<span style={{color:'var(--text3)'}}>checking matches...</span>:<><span style={{color:'var(--green)',marginRight:8}}>✓ {matched.length} matched</span>{unmatched.length>0&&<span style={{color:'var(--red)'}}>✗ {unmatched.length} unmatched</span>}</>}</div>
              </div>;
            })}
          </div>
          {selStatement&&(()=>{
            const {matched,unmatched,bookOnly}=getMatches(selStatement);
            const diff = (bookBal===null||bookBal===undefined) ? null : Number(selStatement.closing_balance||0) - bookBal;
            return <div>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:15}}>{selStatement.bank_name} — {fmtDMY(selStatement.statement_date)}</div>
                <select style={{fontSize:12,marginLeft:'auto'}} value={acctFilter} onChange={e=>setAcctFilter(e.target.value)} title="Compare against one account in your cash book">
                  <option value="">Compare with: all accounts</option>
                  {modeList.map(m=><option key={m} value={m}>Compare with: {m}</option>)}
                </select>
                {canEdit('accounts')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>deleteStatement(selStatement)}><Trash2 size={13}/> Delete</button>}
              </div>
              <div className="stats-grid" style={{marginBottom:12}}>
                {[{label:'Opening',val:`₹${(selStatement.opening_balance||0).toLocaleString('en-IN')}`,color:'var(--accent)'},{label:'Closing (bank)',val:`₹${(selStatement.closing_balance||0).toLocaleString('en-IN')}`,color:'var(--accent)'},
                  ...(acctFilter?[{label:`Book balance (${acctFilter})`,val:bookBal===undefined?'calculating...':INR(bookBal),color:'var(--accent)'},{label:'Difference',val:diff===null?'—':INR(diff),color:diff!==null&&Math.abs(diff)<1?'var(--green)':'var(--red)'}]:[]),
                  {label:'Matched',val:matched.length,color:'var(--green)'},{label:'Unmatched',val:unmatched.length,color:'var(--red)'}].map(s=><div key={s.label} className="stat-card" style={{padding:10,textAlign:'center'}}><div style={{fontSize:16,fontWeight:800,color:s.color}}>{s.val}</div><div style={{fontSize:10,color:'var(--text3)'}}>{s.label}</div></div>)}
              </div>
              {unmatched.length>0&&<div style={{marginBottom:16}}>
                <div style={{fontWeight:700,color:'var(--red)',marginBottom:8}}>❌ Unmatched ({unmatched.length})</div>
                <div className="table-wrap"><table style={{fontSize:12}}><thead><tr style={{background:'rgba(239,68,68,0.1)'}}><th>Date</th><th>Description</th><th>Amount</th><th>Type</th></tr></thead>
                <tbody>{unmatched.map((e,i)=><tr key={i} style={{borderBottom:'1px solid var(--border)'}}><td style={{padding:'5px 10px'}}>{fmtDMY(e.bank.date)}</td><td style={{padding:'5px 10px'}}>{e.bank.description}</td><td style={{padding:'5px 10px',fontWeight:600}}>₹{Math.abs(e.bank.amount||0).toLocaleString('en-IN')}</td><td style={{padding:'5px 10px'}}><span className={`badge ${e.bank.type==='credit'?'badge-green':'badge-red'}`}>{e.bank.type}</span></td></tr>)}</tbody></table></div>
              </div>}
              {matched.length>0&&<div>
                <div style={{fontWeight:700,color:'var(--green)',marginBottom:8}}>✅ Matched ({matched.length})</div>
                <div className="table-wrap"><table style={{fontSize:12}}><thead><tr style={{background:'rgba(34,197,94,0.1)'}}><th>Date</th><th>Bank</th><th>Book</th><th>Amount</th></tr></thead>
                <tbody>{matched.map((e,i)=><tr key={i} style={{borderBottom:'1px solid var(--border)'}}><td style={{padding:'5px 10px'}}>{fmtDMY(e.bank.date)}</td><td style={{padding:'5px 10px'}}>{e.bank.description}</td><td style={{padding:'5px 10px',color:'var(--text3)'}}>{e.book.description}</td><td style={{padding:'5px 10px',fontWeight:600}}>₹{Math.abs(e.bank.amount||0).toLocaleString('en-IN')}</td></tr>)}</tbody></table></div>
              </div>}
              {bookOnly.length>0&&<div style={{marginTop:16}}>
                <div style={{fontWeight:700,color:'var(--amber)',marginBottom:8}}>📒 In your books but not on the bank statement ({bookOnly.length})</div>
                <div className="table-wrap"><table style={{fontSize:12}}><thead><tr style={{background:'rgba(245,158,11,0.1)'}}><th>Date</th><th>Type</th><th>Description</th><th>Amount</th></tr></thead>
                <tbody>{bookOnly.map(t=><tr key={t.id} style={{borderBottom:'1px solid var(--border)'}}><td style={{padding:'5px 10px'}}>{fmtDMY(t.date)}</td><td style={{padding:'5px 10px'}}>{t.type}</td><td style={{padding:'5px 10px'}}>{t.description||'—'}</td><td style={{padding:'5px 10px',fontWeight:600}}>₹{Number(t.amount||0).toLocaleString('en-IN')}</td></tr>)}</tbody></table></div>
              </div>}
            </div>;
          })()}
        </div>
      )}
      {showModal&&<div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
        <div className="modal modal-xl">
          <div className="modal-header"><span className="modal-title">Upload Bank Statement</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid" style={{marginBottom:16}}>
              <div className="form-group"><label>Bank Name *</label><input value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))}/></div>
              <div className="form-group"><label>Account Number</label><input value={form.account_number||''} onChange={e=>setForm(f=>({...f,account_number:e.target.value}))}/></div>
              <div className="form-group"><label>Statement Date *</label><DateInput value={form.statement_date} onChange={e=>setForm(f=>({...f,statement_date:e.target.value}))}/></div>
              <div className="form-group"><label>Opening Balance (₹)</label><input type="number" value={form.opening_balance||''} onChange={e=>setForm(f=>({...f,opening_balance:e.target.value}))}/></div>
              <div className="form-group"><label>Closing Balance (₹)</label><input type="number" value={form.closing_balance||''} onChange={e=>setForm(f=>({...f,closing_balance:e.target.value}))}/></div>
            </div>
            <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>Bank Entries <button className="btn btn-ghost btn-sm" style={{marginLeft:8}} onClick={()=>setBankEntries(e=>[...e,{date:'',description:'',amount:0,type:'credit'}])}>+ Row</button>
              <button className="btn btn-secondary btn-sm" style={{marginLeft:8}} onClick={()=>stmtFileRef.current?.click()}><Upload size={13}/> Load from Excel/CSV</button>
              <input ref={stmtFileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={loadStatementFile}/></div>
            <div className="table-wrap"><table style={{fontSize:12}}><thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Type</th><th></th></tr></thead>
            <tbody>{bankEntries.map((e,i)=><tr key={i}>
              <td style={{padding:4}}><DateInput style={{width:'100%',fontSize:12}} value={e.date} onChange={ev=>setBankEntries(es=>es.map((x,j)=>j===i?{...x,date:ev.target.value}:x))}/></td>
              <td style={{padding:4}}><input style={{width:'100%',fontSize:12}} value={e.description} onChange={ev=>setBankEntries(es=>es.map((x,j)=>j===i?{...x,description:ev.target.value}:x))} placeholder="Description"/></td>
              <td style={{padding:4}}><input type="number" style={{width:100,fontSize:12}} value={e.amount} onChange={ev=>setBankEntries(es=>es.map((x,j)=>j===i?{...x,amount:Number(ev.target.value)}:x))}/></td>
              <td style={{padding:4}}><select style={{fontSize:12}} value={e.type} onChange={ev=>setBankEntries(es=>es.map((x,j)=>j===i?{...x,type:ev.target.value}:x))}><option value="credit">Credit</option><option value="debit">Debit</option></select></td>
              <td style={{padding:4}}><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>setBankEntries(es=>es.filter((_,j)=>j!==i))}>✕</button></td>
            </tr>)}</tbody></table></div>
          </div>
          <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button><button className="btn btn-primary" onClick={saveStatement} disabled={saving}>{saving?'Saving...':'Save Statement'}</button></div>
        </div>
      </div>}
    </div>
  );
}


// ══ AUDIT LOG & MONTH LOCK ══
const AUDIT_SETUP_SQL = `create table if not exists accounts_audit_log (
  id bigserial primary key,
  created_at timestamptz default now(),
  action text, entity text, entity_id text, company text, summary text,
  before_data jsonb, after_data jsonb, changed_by text
);
create table if not exists accounts_period_locks (
  company text primary key,
  locked_through date,
  updated_by text,
  updated_at timestamptz default now()
);`;

function AuditAndLocks({ company, user }) {
  const [lockedThrough, setLockedThrough] = useState(null);
  const [lockInput, setLockInput] = useState('');
  const [lockAvail, setLockAvail] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logAvail, setLogAvail] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true);
    const lk = await supabase.from('accounts_period_locks').select('locked_through').eq('company', company).maybeSingle();
    if (lk.error) setLockAvail(false);
    else { setLockAvail(true); setLockedThrough(lk.data?.locked_through || null); setLockInput(lk.data?.locked_through || ''); }
    const lg = await supabase.from('accounts_audit_log').select('*').or(`company.eq.${company},company.is.null`).order('created_at', { ascending: false }).limit(300);
    if (lg.error) { setLogAvail(false); setLogs([]); } else { setLogAvail(true); setLogs(lg.data || []); }
    setLoading(false);
  }
  useEffect(() => { load(); }, [company]);

  async function saveLock(date) {
    if (date && lockedThrough && date < lockedThrough && !window.confirm(`This moves the lock BACK from ${lockedThrough} to ${date}, re-opening those dates for changes. Continue?`)) return;
    if (!date && !window.confirm('Remove the lock completely? Any past entry will become editable again.')) return;
    setSaving(true);
    const { error } = await supabase.from('accounts_period_locks').upsert(
      { company, locked_through: date || null, updated_by: user?.email || null, updated_at: new Date().toISOString() }, { onConflict: 'company' });
    if (error) toast.error('Could not save: ' + error.message);
    else {
      _lockOk = true;
      logAudit(user, date ? 'lock' : 'unlock', 'period_lock', { company }, { summary: date ? `Books locked through ${date}` : 'Lock removed', before: { locked_through: lockedThrough }, after: { locked_through: date || null } });
      toast.success(date ? `Books locked through ${date}` : 'Lock removed');
      load();
    }
    setSaving(false);
  }

  const q = search.trim().toLowerCase();
  const shown = logs.filter(l => !q || [l.action, l.entity, l.summary, l.changed_by].some(v => String(v || '').toLowerCase().includes(q)));
  const lastDayOfPrevMonth = (() => { const d = new Date(); return dateStrOf(new Date(d.getFullYear(), d.getMonth(), 0)); })();

  if (loading) return <div className="loading"><div className="spinner"/></div>;
  return (
    <div>
      {(!lockAvail || !logAvail) && (
        <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#92400e'}}>
          <div style={{fontWeight:700,marginBottom:6}}>One-time setup needed</div>
          Run this in the Supabase SQL editor, then reload this tab{!logAvail&&!lockAvail?'':' (only the missing part is needed)'}:
          <pre style={{background:'rgba(0,0,0,0.05)',padding:8,borderRadius:6,marginTop:6,whiteSpace:'pre-wrap',fontSize:11}}>{AUDIT_SETUP_SQL}</pre>
        </div>
      )}

      <div className="card" style={{marginBottom:16,padding:14}}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:6}}>🔒 Month-end lock — {company}</div>
        <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
          Entries dated on or before the lock date cannot be added, edited or deleted from this app (cash book, import, transfers, expense approvals, reminders).
        </div>
        {lockAvail ? (
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:13}}>{lockedThrough ? <>Locked through <strong>{lockedThrough}</strong></> : <strong>Not locked</strong>}</span>
            {isApproverUser(user) && <>
              <DateInput value={lockInput} onChange={e=>setLockInput(e.target.value)} style={{width:150,fontSize:12}}/>
              <button className="btn btn-primary btn-sm" disabled={saving||!lockInput||lockInput===lockedThrough} onClick={()=>saveLock(lockInput)}>Set lock date</button>
              <button className="btn btn-secondary btn-sm" disabled={saving} onClick={()=>saveLock(lastDayOfPrevMonth)}>Lock through last month ({lastDayOfPrevMonth})</button>
              {lockedThrough && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} disabled={saving} onClick={()=>saveLock(null)}>Remove lock</button>}
            </>}
          </div>
        ) : <div style={{fontSize:12,color:'var(--text3)'}}>Not available until the setup SQL above has been run.</div>}
      </div>

      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:14}}>🛡 Audit log</span>
        <div className="search-bar" style={{minWidth:220}}><Search size={14}/><input placeholder="Search action, who, details..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
        <span style={{fontSize:11,color:'var(--text3)'}}>{shown.length} of latest {logs.length}</span>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/> Refresh</button>
      </div>
      {!logAvail ? <div style={{fontSize:12,color:'var(--text3)'}}>Not available until the setup SQL above has been run.</div> : (
        <div className="table-wrap">
          <table style={{fontSize:12}}>
            <thead><tr style={{background:'var(--accent-light)'}}><th>When</th><th>Who</th><th>Action</th><th>What</th></tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No audit entries yet</td></tr>}
              {shown.map(l => (
                <tr key={l.id} style={{borderBottom:'1px solid var(--border)'}} title={l.before_data || l.after_data ? JSON.stringify({ before: l.before_data, after: l.after_data }, null, 1) : ''}>
                  <td style={{padding:'6px 10px',whiteSpace:'nowrap'}}>{new Date(l.created_at).toLocaleString('en-IN')}</td>
                  <td style={{padding:'6px 10px'}}>{l.changed_by || '—'}</td>
                  <td style={{padding:'6px 10px'}}><span className={`badge ${l.action==='delete'||l.action==='reject'?'badge-red':l.action==='update'?'badge-amber':'badge-blue'}`}>{l.action}</span> <span style={{color:'var(--text3)'}}>{l.entity}</span></td>
                  <td style={{padding:'6px 10px'}}>{l.summary || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
